// SEO Landing Pages content config
// Each page targets a specific head/long-tail keyword cluster.

export interface LandingFAQ {
  q: string;
  a: string;
}

export interface LandingSection {
  heading: string;
  body: string; // Plain text paragraphs separated by \n\n
}

export interface RelatedLink {
  label: string;
  href: string;
}

export interface LandingPageContent {
  slug: string; // route path without leading slash
  primaryKeyword: string;
  title: string; // <title> tag
  metaDescription: string; // <meta description>, <=160 chars
  h1: string;
  heroSubtitle: string;
  heroImageAlt: string;
  // Slugs of categories to query products from (matches `categories.slug`)
  categorySlugs: string[];
  // Optional fallback keywords matched against product name/tags if no category match
  productKeywords?: string[];
  intro: string; // Lead paragraph under H1
  sections: LandingSection[]; // Editorial body sections (H2)
  faqs: LandingFAQ[];
  related: RelatedLink[];
}

export const LANDING_PAGES: LandingPageContent[] = [
  // 1. HIJABS
  {
    slug: "hijabs",
    primaryKeyword: "hijabs",
    title: "Hijabs — Chiffon, Jersey, Silk & Magnetic | HijabiStore",
    metaDescription:
      "Buy premium hijabs online — chiffon, jersey, silk, modal & magnetic styles in 50+ colors. Free US shipping over $75 from vetted vendors. 30-day returns.",
    h1: "Hijabs — Chiffon, Jersey, Silk, Modal & Magnetic Styles",
    heroSubtitle:
      "Premium hijabs from independent vendors. Find your perfect fabric, length, and style — backed by free US shipping over $75 and 30-day returns.",
    heroImageAlt: "Selection of premium chiffon, jersey and silk hijabs in soft neutral tones",
    categorySlugs: ["hijabs", "hijab"],
    productKeywords: ["hijab", "scarf", "shawl"],
    intro:
      "Finding the right hijab is honestly about matching the fabric to the kind of day you're having — and we say that as a hijabi store run by people who actually wear hijab every day. Our hijab shop carries 100+ styles across jersey, chiffon, silk, modal, cotton and magnetic finishes in 50+ colors, all from independent modest-fashion designers (no mass-produced factory fabric here). Whether you're a brand-new hijabi looking for your first stay-put scarf or a long-time wearer building out an event wardrobe, this is the corner of the hijabi store designed to help you choose with confidence.",
    sections: [
      {
        heading: "Your guide to choosing the perfect hijab",
        body:
          "If you ask any hijabi which fabric to start with, almost all of us will say jersey — it's stretchy, grippy, doesn't need pins, and forgives every mistake. It's the hijab equivalent of training wheels, except plenty of us never take them off. Chiffon hijabs are lighter and dressier, and they're what we reach for when there's an event in the calendar — Eid lunches, nikkahs, graduations. Silk hijabs add that quiet luxury for the days you want to feel a little more elevated, and modal sits in the sweet spot between the two: soft, breathable, properly comfortable for all-day wear.\n\nNot sure where to start? Our quick rule from the hijab shop floor: jersey for ease, chiffon for summer and events, modal for the premium feel without the silk price tag, and magnetic styles if you're tired of constantly adjusting. Build a base of 5–7 neutrals first (black, white, nude, dusty rose), then layer in colors and prints from there.",
      },
      {
        heading: "Square vs rectangle: which hijab shape do you need?",
        body:
          "Rectangle hijabs (around 27\" x 70\" up to 30\" x 75\") are the most versatile shape we stock — they handle wrapped styles, loose drapes, turbans and pretty much everything in between. Square hijabs (about 45\" x 45\") fold into a triangle for fuller coverage and are the go-to for traditional and Khaleeji styles. Maxi hijabs over 80\" long give you extra fabric to drape across the chest — popular with sisters who want a little more coverage without layering.",
      },
      {
        heading: "Why buy hijabs from an independent hijabi store?",
        body:
          "Every hijab in our hijab shop is hand-picked from a small modest-fashion designer or independent brand — that means you're getting unique cuts, considered fabric choices and details that mass-market hijab shops just don't offer. We test for opacity, dye quality, and how the fabric wears under a long day before anything goes live. And because we're a hijabi store first, we know which scarves work in 90-degree summers, which ones survive a school run with a toddler, and which ones photograph beautifully at a wedding.",
      },
      {
        heading: "Caring for your hijabs",
        body:
          "Chiffon and silk: hand wash cold with gentle detergent, never wring, lay flat to dry. Jersey, modal and cotton: machine wash cold on delicate, then tumble dry low or hang to air. Store flat or on padded hangers — wire hangers crease the fabric and shorten the life of your nicer pieces.",
      },
    ],
    faqs: [
      {
        q: "What is the best hijab fabric for beginners?",
        a: "Jersey, every time. It's stretchy, stays in place without pins, and is incredibly forgiving with styling. Start with a neutral like black, white or nude and you'll have a versatile foundation that works with most of your wardrobe.",
      },
      {
        q: "How do I keep my hijab from slipping?",
        a: "Three options that actually work: wear an undercap for grip, switch to a naturally grippier fabric like jersey or modal, or use magnetic hijab pins. Our magnetic hijabs have built-in solutions so they stay put all day with zero fussing.",
      },
      {
        q: "What hijab color goes with everything?",
        a: "Black, white, nude/beige and dusty rose are the most versatile hijab colors. Most hijabis we serve recommend building a base collection of 5–7 neutral hijabs from the hijab shop before adding bold colors and prints.",
      },
      {
        q: "Do you offer hijab bundles or matching sets?",
        a: "Yes — check our matching hijab sets section for coordinated hijab and undercap pairings, plus we frequently run bundle deals on our bestselling fabrics.",
      },
      {
        q: "Are magnetic hijabs really pin-free?",
        a: "Yes — magnetic hijab clips use small magnets that hold layers without piercing the fabric, so they're safe even for delicate chiffon and silk.",
      },
      {
        q: "Do you ship hijabs internationally?",
        a: "Yes, we ship to 100+ countries from our US-based hijabi store. Free standard US shipping on orders over $75.",
      },
    ],
    related: [
      { label: "Black hijabs", href: "/black-hijabs" },
      { label: "White hijabs", href: "/white-hijabs" },
      { label: "Nude & neutral hijabs", href: "/nude-hijabs" },
      { label: "Modal hijabs", href: "/modal-hijabs" },
      { label: "Underscarves", href: "/underscarves" },
      { label: "Abayas", href: "/abayas" },
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "Hijab style quiz", href: "/quiz" },
    ],
  },

  // 2. ABAYAS
  {
    slug: "abayas",
    primaryKeyword: "abayas",
    title: "Abayas: Open, Closed, Kimono & Formal Abayas | HijabiStore",
    metaDescription:
      "Shop abayas online — open-front, closed, kimono, butterfly & formal embroidered styles. Free US shipping over $75. Vetted modest fashion brands.",
    h1: "Abayas — Open, Closed, Kimono, Butterfly & Formal Styles",
    heroSubtitle:
      "Discover abayas for every occasion: everyday open-fronts, formal embroidered abayas, butterfly silhouettes, and kimono cuts from independent modest designers.",
    heroImageAlt: "Black formal abaya with subtle embroidery photographed on a neutral background",
    categorySlugs: ["abayas", "abaya"],
    productKeywords: ["abaya", "kaftan", "robe"],
    intro:
      "Abayas have come a long way from \"basic black\" — and our hijabi store is honestly proof of that. Today's abaya wardrobe spans open-front layering pieces, dramatic butterfly cuts, structured kimonos, embellished kaftans for nikkah season, and travel-friendly jerseys for the airport. The trick is matching the style to the occasion, and that's where the hijabi shop floor experience helps. Every abaya we list comes from an independent modest designer — people who care about fabric weight, opacity, sleeve length and how a hem actually drapes — so you don't have to second-guess what you're getting.",
    sections: [
      {
        heading: "How to choose the right abaya",
        body:
          "For everyday wear, a closed abaya in a lightweight nida or crepe keeps things simple and doesn't crease in the car. Open-front abayas are the most versatile piece in any hijabi wardrobe — wear them like a long coat over jeans and a top, throw one on for school runs, layer one over a dress for dinner. Kimono abayas blend modest coverage with that relaxed, modern silhouette a lot of us reach for on weekends.\n\nFor occasions, butterfly abayas make a quiet statement with their wide flowing sleeves, and embroidered or beaded abayas are what our customers reach for when there's a wedding, an Eid lunch or a graduation in the diary. For Umrah and Hajj, we always point sisters toward our lightweight white or stone closed abayas with zero metallic detail — practical, breathable, and respectful of the ihram context.",
      },
      {
        heading: "Abaya fabrics & how they wear",
        body:
          "Nida and Korean nida are the gold standard for daily abayas — weighty, beautifully draping, wrinkle-resistant. Crepe is slightly lighter with a subtle texture and works well for formal pieces. Jersey abayas are stretchy and packable, which is why they live permanently in most hijabis' carry-ons. Chiffon and georgette abayas are airy and elegant for warm-weather events, but layer them over a slip for full opacity.",
      },
      {
        heading: "Sizing, length & why we curate carefully",
        body:
          "Standard abaya lengths run 54–58 inches; tall sizes go up to 60–62 inches. Measure shoulder-to-ankle in a straight line and choose the length that ends at the top of your foot for a clean drape. Plus sizing is widely available across our independent designers — most of our brands stock through US 24+ and many offer petite and tall options. Because this is a hijabi store and not a generic marketplace, we only list abayas we'd genuinely wear ourselves.",
      },
    ],
    faqs: [
      {
        q: "What's the difference between open and closed abayas?",
        a: "Closed abayas slip over your head and provide full coverage as a single garment — clean and streamlined. Open abayas have an open front and are worn like a long coat or cardigan over your outfit, making them the most versatile piece for layering.",
      },
      {
        q: "How should an abaya fit?",
        a: "An abaya should be comfortable and never restrictive — most styles are designed to be loose-fitting on purpose. For length, aim for ankle-length or just above. If you're between sizes, our hijabi store advice is always to size up for a more relaxed, flattering drape.",
      },
      {
        q: "Can I wear an abaya to work?",
        a: "Absolutely. A structured closed abaya or a tailored kimono abaya in a neutral color reads as polished, professional outerwear. Pair it with a clean hijab from our hijab shop and minimal jewellery and you're set for the office.",
      },
      {
        q: "What's the difference between an abaya and a jilbab?",
        a: "An abaya is a loose, ankle-length outer robe (open or closed front). A jilbab traditionally refers to a longer, looser garment that may be one piece including the head covering — usage varies by region and culture.",
      },
      {
        q: "Are abayas only black?",
        a: "Not at all. Modern abayas come in every color — browns, taupes, sages, blush, navy and deep jewel tones are all popular in our hijabi store, both for everyday and formal occasions.",
      },
      {
        q: "How do I wash an abaya?",
        a: "Most nida and crepe abayas can be machine-washed cold on delicate and hung to dry. Embroidered or beaded pieces should always be dry-cleaned to protect the embellishment.",
      },
    ],
    related: [
      { label: "Kaftans", href: "/kaftans" },
      { label: "Jilbabs & khimars", href: "/jilbabs-khimars" },
      { label: "Modest evening wear", href: "/modest-eveningwear" },
      { label: "Hijabs", href: "/hijabs" },
    ],
  },

  // 3. MODEST DRESSES
  {
    slug: "modest-dresses",
    primaryKeyword: "modest dresses",
    title: "Modest Dresses — Maxi, Work & Wedding Guest | HijabiStore",
    metaDescription:
      "Shop modest dresses — maxi, long-sleeve, work, evening & wedding guest styles. Full coverage with elegant fit. Free US shipping over $75.",
    h1: "Modest Dresses — Maxi, Work, Evening & Wedding Guest",
    heroSubtitle:
      "Long-sleeve, full-coverage dresses cut for real life: from everyday maxi dresses to wedding-ready evening gowns, all from vetted modest designers.",
    heroImageAlt: "Long-sleeve modest maxi dress in a soft neutral tone",
    categorySlugs: ["modest-dresses", "dresses"],
    productKeywords: ["modest dress", "maxi dress", "long sleeve dress"],
    intro:
      "Finding modest dresses that are actually modest — long sleeves, ankle length, opaque fabric, no slits — used to mean compromising on style. Not anymore. Our modest dresses are designed by people who wear them: relaxed maxi dresses for daily life, structured midis for work, and floor-length gowns for evenings, weddings, and Eid.",
    sections: [
      {
        heading: "Modest dress styles for every occasion",
        body:
          "Maxi dresses with long sleeves are the modest wardrobe workhorse — easy to layer with a cardigan or open-front abaya.\n\nWork-friendly midi dresses with sleeves and a relaxed silhouette pair well with tailored outerwear.\n\nEvening gowns and wedding-guest dresses with covered shoulders, defined waists, and floor-grazing hems make modest dressing feel luxurious.\n\nShirt dresses and prairie styles offer easy everyday modesty without effort.",
      },
      {
        heading: "What makes a dress truly modest?",
        body:
          "Look for: long sleeves (or three-quarter at minimum), an ankle- or floor-length hem, opaque fabric (no see-through chiffon without a slip), a high neckline, and no thigh-high slits.\n\nWe screen our listings to ensure they meet modest-fashion standards before they appear on site.",
      },
      {
        heading: "Best fabrics for modest dresses",
        body:
          "Crepe and ponte give structure and resist wrinkles — ideal for work and travel.\n\nCotton, linen, and modal are breathable for hot weather.\n\nSatin, silk, and chiffon (lined) bring elegance for evening events.\n\nKnits and jerseys are forgiving and great for everyday comfort.",
      },
      {
        heading: "Layering modest dresses",
        body:
          "Layer a long-sleeve fitted top under a sleeveless maxi to create modest coverage instantly.\n\nAdd an open-front abaya, kimono, or duster over a midi dress for instant occasion-wear.\n\nA hijab in a complementary tone ties the look together — neutrals (taupe, ivory, black) work with everything.",
      },
    ],
    faqs: [
      {
        q: "What is considered a modest dress?",
        a: "A modest dress typically has long sleeves, an ankle or floor-length hem, opaque fabric, a high neckline, and no thigh-high slits.",
      },
      {
        q: "Where can I find long-sleeve maxi dresses?",
        a: "Browse our modest dresses collection — every piece meets modest-fashion standards (long sleeves, ankle length, opaque fabric).",
      },
      {
        q: "Are these dresses suitable for hijab wearers?",
        a: "Yes — every dress is screened for hijab-friendly necklines and sleeve coverage.",
      },
      {
        q: "Can I wear modest dresses to a wedding?",
        a: "Absolutely. Our evening and wedding-guest dresses come in elegant gowns, embellished maxis, and silk styles cut for full coverage.",
      },
      {
        q: "Do you offer plus-size modest dresses?",
        a: "Yes. Many vendors carry sizes through US 24+ and offer petite and tall options.",
      },
      {
        q: "How do modest dresses fit?",
        a: "Most of our vendors run true to standard US sizing, but each listing includes a size chart. Check vendor sizing notes before ordering.",
      },
    ],
    related: [
      { label: "Modest evening wear", href: "/modest-eveningwear" },
      { label: "Modest workwear", href: "/modest-workwear" },
      { label: "Abayas", href: "/abayas" },
      { label: "Modest tops & tunics", href: "/modest-tops-tunics" },
    ],
  },

  // 4. MODEST ACTIVEWEAR
  {
    slug: "modest-activewear",
    primaryKeyword: "modest activewear",
    title: "Modest Activewear & Gym Clothes | HijabiStore",
    metaDescription:
      "Shop modest activewear and sports hijabs for working out, running & yoga. Full-coverage gym clothes designed for Muslim women. Free US shipping on $75+.",
    h1: "Modest Activewear & Gym Clothes for Muslim Women",
    heroSubtitle:
      "Performance-grade modest workout wear: sports hijabs, full-length leggings, long-sleeve tops, and modest gym sets that actually move with you.",
    heroImageAlt: "Modest workout set with sports hijab, long-sleeve top and full-length leggings",
    categorySlugs: ["modest-activewear", "activewear", "sportswear"],
    productKeywords: ["activewear", "sports", "workout", "gym", "yoga", "athletic"],
    intro:
      "Working out in modest clothing used to mean baggy t-shirts, overheating mid-set and hijabs that needed re-pinning every five minutes — every hijabi knows the drill. Modern modest activewear is a completely different story, and our hijabi store curates the brands that actually get it right: sweat-wicking fabrics, four-way stretch, breathable mesh panels, UPF sun protection. Whether you're running, lifting, doing yoga or finally giving padel a try, there's modest gym wear engineered for it — and we only list pieces from brands designed by and for Muslim women who actually train.",
    sections: [
      {
        heading: "Modest activewear that moves with you",
        body:
          "Sports hijabs are the foundation of any modest workout outfit. They're cut from lightweight, quick-dry technical fabrics that stay put through movement without pins, clips or constant adjusting. Pair a good sports hijab with full-length leggings and a long-sleeve performance top, and you've got a complete outfit that delivers full coverage and zero compromise on function.\n\nFrom the hijab shop side of things, the difference between a regular hijab and a proper sports hijab is huge — the technical jersey grips, the seams sit flat against the head, and the cape lengths are designed to tuck into a top without riding up. Once you've trained in a sports hijab, you won't go back.",
      },
      {
        heading: "What to look for in modest gym clothes",
        body:
          "For leggings: high waist, four-way stretch, opaque fabric. Most of our independent designers specifically test for squat-proof opacity before they list with us. For tops: long sleeves, dropped or curved hems for extra hip coverage, and thumbholes if you want extra cuff coverage during burpees and downward dog. For gym sets: matching top and bottom takes the decision-making out of early-morning workouts.",
      },
      {
        heading: "Best fabrics for modest workout wear",
        body:
          "Polyester-spandex blends offer the best moisture management and durability — our top pick for high-intensity training and outdoor running. Nylon-spandex feels softer against the skin and is great for yoga, pilates and lower-impact sessions. Technical bamboo and modal blends are gentle on sensitive skin and work beautifully in hot studios. Avoid cotton for workouts — it holds sweat and gets heavy fast.",
      },
      {
        heading: "Modest swim & beachwear",
        body:
          "Sports hijabs are designed for land workouts, not the pool. If you're shopping for swim, head over to our modest swimwear collection — burkinis, dedicated swim hijabs and full-coverage swim sets in chlorine-resistant, quick-dry technical fabrics.",
      },
    ],
    faqs: [
      {
        q: "What should I wear to the gym as a hijabi?",
        a: "A sports hijab, long-sleeve performance top and full-length leggings is the standard hijabi gym outfit. Look for sweat-wicking fabrics and four-way stretch — and avoid cotton, because it holds sweat and gets heavy fast.",
      },
      {
        q: "Can I swim in a sports hijab?",
        a: "Sports hijabs are designed specifically for land workouts and won't perform in chlorinated water. For swimming, our hijabi store stocks a separate modest swimwear section with burkinis and swim-specific hijabs cut from chlorine-resistant, quick-dry fabrics.",
      },
      {
        q: "Are your activewear pieces squat-proof?",
        a: "We curate from independent designers who prioritize opacity and coverage, and product descriptions note fabric weight and opacity level so you can choose confidently. Anything tagged squat-proof has been specifically tested.",
      },
      {
        q: "What is the best sports hijab for high-impact training?",
        a: "Look for an instant (pull-on) sports hijab in a moisture-wicking polyester or technical jersey blend with a snug, grippy fit. The instant style stays locked in through running, HIIT and inversions without any pinning.",
      },
      {
        q: "Where can I buy modest workout clothes for Muslim women in the US?",
        a: "Right here — every vendor in our modest activewear collection ships within the US, with free standard shipping on orders over $75 and 30-day returns through the hijabi store.",
      },
    ],
    related: [
      { label: "Modest swimwear", href: "/modest-swimwear" },
      { label: "Hijabs", href: "/hijabs" },
      { label: "Underscarves & hijab caps", href: "/underscarves-hijab-caps" },
      { label: "Modest tops & tunics", href: "/modest-tops-tunics" },
    ],
  },

  // 5. MODEST SWIMWEAR
  {
    slug: "modest-swimwear",
    primaryKeyword: "modest swimwear",
    title: "Modest Swimwear & Burkinis for Women | HijabiStore",
    metaDescription:
      "Shop modest swimwear & burkinis — full-coverage swim sets, swim hijabs, swim leggings & long-sleeve rash guards. Chlorine-resistant. Free US shipping over $75.",
    h1: "Modest Swimwear & Burkinis — Full-Coverage Swim Sets",
    heroSubtitle:
      "Burkinis, swim hijabs, swim leggings, and long-sleeve rash guards. UPF-rated, chlorine-resistant, and built for the pool, beach, and ocean.",
    heroImageAlt: "Two-piece burkini with swim hijab on neutral background",
    categorySlugs: ["modest-swimwear", "swimwear", "burkini"],
    productKeywords: ["swim", "burkini", "swimwear", "rash guard"],
    intro:
      "Modest swimwear has come a long way — modern burkinis are lightweight, quick-drying, UPF-protective, and actually flattering. Whether you want a two-piece swim set with leggings and tunic, a one-piece burkini with attached hood, or separates to mix and match, this collection covers every preference.",
    sections: [
      {
        heading: "Types of modest swimwear",
        body:
          "Burkini (two-piece): swim leggings + long tunic top + integrated or separate swim hijab. Most popular all-rounder.\n\nFull-piece burkini: connected one-piece with attached head covering and built-in tunic.\n\nSwim leggings + rash guard: separates for layered modest looks, often paired with a swim skirt.\n\nSwim hijabs: technical caps designed to stay in place in water and dry quickly.",
      },
      {
        heading: "Best fabrics for modest swim",
        body:
          "Polyester-spandex blends with chlorine resistance hold up best in pools.\n\nNylon-lycra blends feel soft and dry quickly — ideal for ocean swims.\n\nLook for UPF 50+ ratings if you'll be in direct sun for extended periods.",
      },
      {
        heading: "Caring for modest swimwear",
        body:
          "Rinse in cool fresh water immediately after pool or ocean use to remove chlorine and salt.\n\nHand wash with mild detergent and lay flat to dry — never wring or tumble dry.\n\nStore folded flat, away from direct sunlight to preserve color and elasticity.",
      },
    ],
    faqs: [
      {
        q: "What is a burkini?",
        a: "A burkini is a modest swimsuit that covers the body, arms, legs, and hair. It's typically a two-piece (leggings + tunic + swim hijab) or a connected full-piece designed for swimming.",
      },
      {
        q: "Are burkinis allowed in public pools?",
        a: "In the US, burkinis are typically welcome at public and private pools as they meet hygiene requirements (similar to rash guards and leggings).",
      },
      {
        q: "What's the best fabric for a burkini?",
        a: "A polyester-spandex blend with chlorine resistance and UPF 50+ protection is the best all-around choice.",
      },
      {
        q: "Do swim hijabs stay on in water?",
        a: "Yes — purpose-built swim hijabs use technical fabric and snug fit to stay in place during swimming and water sports.",
      },
      {
        q: "Where can I buy modest swimwear in the US?",
        a: "Our modest swimwear collection features US-based and international vendors with fast shipping and 30-day returns.",
      },
    ],
    related: [
      { label: "Burkini", href: "/burkini" },
      { label: "Modest activewear", href: "/modest-activewear" },
      { label: "Hijabs", href: "/hijabs" },
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "Underscarves", href: "/underscarves" },
    ],
  },

  // 6. PRAYER DRESSES
  {
    slug: "prayer-dresses",
    primaryKeyword: "prayer dresses",
    title: "Prayer Dresses & One-Piece Prayer Sets | HijabiStore",
    metaDescription:
      "Shop prayer dresses & prayer sets — full-coverage one-piece prayer abayas and garments for prayer and daily wear. Free US shipping on $75+.",
    h1: "Prayer Dresses & One-Piece Prayer Sets",
    heroSubtitle:
      "Easy slip-on prayer dresses, one-piece sets, and travel prayer garments — designed for quick wudu-to-salah transitions at home, work, or on the go.",
    heroImageAlt: "Soft white one-piece prayer dress with attached head covering",
    categorySlugs: ["prayer-dresses", "prayer-wear", "prayer"],
    productKeywords: ["prayer", "salah", "isdal", "namaz"],
    intro:
      "A good prayer dress (isdal, namaz dress, or salah set) lets you go from wudu to salah in seconds — no pins, no layering, no rush. We carry one-piece designs, two-piece sets, and packable travel prayer garments from modest fashion vendors who understand exactly what's needed for daily prayer.",
    sections: [
      {
        heading: "Prayer garment styles",
        body:
          "One-piece prayer dress: a single full-length garment with attached head covering. Pull on, tighten, and pray. Most popular at home.\n\nTwo-piece prayer set: a long skirt + a top with built-in headcover. More flexible for taller frames and easier to wash.\n\nTravel prayer garment: lightweight, packable, often comes in a pouch — perfect for the office, travel, or visiting non-Muslim homes.",
      },
      {
        heading: "What to look for in a prayer dress",
        body:
          "Opaque fabric — usually a cotton-poly blend or soft polyester crepe.\n\nElastic or drawstring waist for a comfortable fit during ruku and sujood.\n\nHeadcover that frames the face fully and doesn't slip during sujood.\n\nMachine washable for easy care after frequent use.",
      },
      {
        heading: "Best for travel and the office",
        body:
          "Lightweight prayer garments fold into a small pouch and weigh next to nothing — keep one in your bag, your car, and your desk drawer.\n\nNeutral colors (white, cream, beige, navy, black) read appropriate in any setting.",
      },
    ],
    faqs: [
      {
        q: "What is a prayer dress?",
        a: "A prayer dress (also called an isdal, namaz dress, or salah set) is a one-piece or two-piece modest garment designed to be quickly worn over regular clothes for daily prayer.",
      },
      {
        q: "Is a one-piece or two-piece prayer set better?",
        a: "One-piece sets are fastest to wear and ideal for home use. Two-piece sets give more flexibility for height and easier washing.",
      },
      {
        q: "Can I bring a prayer dress to work?",
        a: "Yes — travel prayer garments fold into a small pouch and are designed for office, travel, and on-the-go prayer.",
      },
      {
        q: "How do I wash a prayer dress?",
        a: "Most prayer dresses are machine washable cold on a gentle cycle. Hang dry or tumble dry low.",
      },
      {
        q: "What size prayer dress should I buy?",
        a: "Most prayer dresses are one-size with elastic adjustments, designed to fit US sizes 4–18. Plus and tall versions are available from many vendors.",
      },
    ],
    related: [
      { label: "Abayas", href: "/abayas" },
      { label: "Jilbabs & khimars", href: "/jilbabs-khimars" },
      { label: "Hijabs", href: "/hijabs" },
      { label: "Modest dresses", href: "/modest-dresses" },
    ],
  },

  // 7. MODEST WORKWEAR
  {
    slug: "modest-workwear",
    primaryKeyword: "modest workwear",
    title: "Modest Workwear & Office Outfits | HijabiStore",
    metaDescription:
      "Shop modest workwear — long-sleeve blouses, midi & maxi work dresses, modest blazers & wide-leg pants. Office-ready modest fashion. Free US shipping over $75.",
    h1: "Modest Workwear & Office Outfits",
    heroSubtitle:
      "Polished modest pieces built for the office: long-sleeve blouses, midi work dresses, blazers, and wide-leg trousers in colors that play well with everything.",
    heroImageAlt: "Modest workwear outfit with long-sleeve blouse, wide-leg trousers and a structured blazer",
    categorySlugs: ["modest-workwear", "workwear", "office"],
    productKeywords: ["work", "office", "blazer", "blouse", "trouser", "professional"],
    intro:
      "Modest workwear shouldn't mean dressing twenty years older. We curate office-ready pieces with long sleeves, full coverage, and modern silhouettes — long-sleeve silk blouses, structured midi dresses, oversized blazers, and wide-leg trousers that look intentional, not improvised.",
    sections: [
      {
        heading: "Building a modest work wardrobe",
        body:
          "Start with three foundations: a long-sleeve blouse in white or cream, a midi work dress in a neutral, and a structured blazer.\n\nAdd wide-leg or straight-leg trousers in black, navy, and a warm neutral.\n\nFinish with two hijabs in matching neutral tones (jersey or modal for daily wear) and a pair of pointed flats or low block heels.",
      },
      {
        heading: "Modest workwear silhouettes that work",
        body:
          "Oversized blazers over a long-sleeve blouse and trousers — sharp without being restrictive.\n\nMidi work dresses with sleeves and a defined waist — pair with closed-toe flats and a complementary hijab.\n\nWide-leg trousers + tucked silk blouse + open-front abaya for a modern modest professional look.\n\nKnit twin-sets (long cardigan + matching shell) for relaxed business-casual settings.",
      },
      {
        heading: "Modest workwear for client-facing roles",
        body:
          "Stick to neutral palettes (black, navy, charcoal, taupe, cream) and well-tailored pieces.\n\nChoose hijab fabrics that match the formality of your industry — silk and satin for finance and law; jersey and modal for tech, healthcare, and education.",
      },
    ],
    faqs: [
      {
        q: "What is modest workwear?",
        a: "Modest workwear refers to professional clothing that provides full coverage — long sleeves, midi or maxi length, opaque fabric, and high necklines — while remaining tailored and office-appropriate.",
      },
      {
        q: "Can I wear an abaya to work?",
        a: "Yes. Open-front abayas in solid neutrals work well as professional outerwear in most industries.",
      },
      {
        q: "What hijab is best for the office?",
        a: "Choose jersey or modal hijabs in solid neutrals for daily wear. Silk and satin work for formal client meetings.",
      },
      {
        q: "Are modest blazers different from regular blazers?",
        a: "Modest blazers tend to be longer (mid-thigh or longer), more relaxed in the shoulder, and styled to layer easily over modest dresses and tunics.",
      },
      {
        q: "What shoes go with modest workwear?",
        a: "Closed-toe flats, pointed flats, low block heels, and ankle boots all pair well with modest work outfits.",
      },
    ],
    related: [
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "Modest tops & tunics", href: "/modest-tops-tunics" },
      { label: "Abayas", href: "/abayas" },
      { label: "Modest skirts", href: "/modest-skirts" },
    ],
  },

  // 8. MODEST TOPS & TUNICS
  {
    slug: "modest-tops-tunics",
    primaryKeyword: "modest tops and tunics",
    title: "Modest Tops, Tunics & Long-Sleeve Blouses | HijabiStore",
    metaDescription:
      "Shop modest tops and tunics — long-line tops, layering pieces & tunics for everyday modest wear. Free US shipping on $75+.",
    h1: "Modest Tops, Tunics & Long-Sleeve Blouses",
    heroSubtitle:
      "Hip-covering tunics, long-sleeve blouses, peplum tops, and layering tees — the modest building blocks for every outfit.",
    heroImageAlt: "Modest long-sleeve tunic top in a soft neutral tone",
    categorySlugs: ["modest-tops", "tops", "tunics", "blouses"],
    productKeywords: ["top", "tunic", "blouse", "shirt", "tee"],
    intro:
      "Modest tops and tunics do the heavy lifting in any modest wardrobe — they layer, they extend, they cover. We carry long-sleeve blouses, hip- and thigh-covering tunics, peplum tops, and seamless layering tees that quietly upgrade every outfit underneath.",
    sections: [
      {
        heading: "Tops & tunics for every layer",
        body:
          "Layering tees: long-sleeve, fitted, and seamless — wear under sleeveless dresses or short-sleeve tops to instantly add coverage.\n\nTunics: hip- or thigh-length, with sleeves and a relaxed cut — pair with leggings, jeans, or wide-leg trousers.\n\nLong-sleeve blouses: silk, cotton, or modal in classic colors — the foundation of any modest workwear capsule.\n\nPeplum tops: structured waist with a flared hem — flattering and modest at the same time.",
      },
      {
        heading: "Best fabrics for modest tops",
        body:
          "Modal and bamboo blends are softest and most breathable for layering.\n\nSilk and satin blouses elevate any outfit for work or evening.\n\nCotton and linen are ideal for hot weather.",
      },
      {
        heading: "How to style modest tops",
        body:
          "Tuck a long-sleeve blouse into wide-leg trousers + open-front abaya = instant modest workwear.\n\nLayer a fitted long-sleeve under a short-sleeve dress = instant modest summer outfit.\n\nWear a flowy tunic over leggings + sneakers = modest casual day look.",
      },
    ],
    faqs: [
      {
        q: "What is a modest tunic?",
        a: "A modest tunic is a longer top — typically hip or thigh length — with sleeves and a relaxed cut, designed to provide modest coverage when worn with leggings, jeans, or trousers.",
      },
      {
        q: "What's the best layering top for hijabis?",
        a: "A seamless, fitted long-sleeve tee in a neutral color is the most versatile layering piece — it adds coverage under any sleeveless or short-sleeve item.",
      },
      {
        q: "Can I wear modest tops with jeans?",
        a: "Yes. Hip- or thigh-length tunics pair perfectly with high-waisted jeans for a modest casual outfit.",
      },
      {
        q: "What size modest top should I order?",
        a: "Most vendors run true to standard US sizing. Check each listing's size chart for vendor-specific guidance.",
      },
      {
        q: "Are modest tops available in plus sizes?",
        a: "Yes — most of our vendors stock modest tops and tunics through US 24+ with extended length options for tall hijabis.",
      },
    ],
    related: [
      { label: "Modest workwear", href: "/modest-workwear" },
      { label: "Modest skirts", href: "/modest-skirts" },
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "Modest activewear", href: "/modest-activewear" },
    ],
  },

  // 9. MODEST SKIRTS
  {
    slug: "modest-skirts",
    primaryKeyword: "modest skirts",
    title: "Modest Maxi & Midi Skirts for Women | HijabiStore",
    metaDescription:
      "Shop modest maxi & midi skirts — pleated, A-line, denim & satin styles in ankle and floor lengths. Full coverage, no slits. Free US shipping over $75.",
    h1: "Modest Maxi & Midi Skirts",
    heroSubtitle:
      "Ankle- and floor-length skirts cut for full coverage — pleated, A-line, denim, satin, and knit. The modest answer to every outfit.",
    heroImageAlt: "Modest pleated maxi skirt in a warm neutral color",
    categorySlugs: ["modest-skirts", "skirts"],
    productKeywords: ["skirt", "maxi skirt", "midi skirt"],
    intro:
      "Modest skirts are the easiest modest wardrobe upgrade — pull on a maxi pleated skirt, tuck in a long-sleeve top, and you're done. We carry maxi and midi skirts in pleated, A-line, denim, satin, and knit options, all cut for ankle or floor coverage with no high slits.",
    sections: [
      {
        heading: "Modest skirt silhouettes",
        body:
          "Pleated maxi skirts in satin or chiffon — flowy, elegant, and a hijabi favorite for events.\n\nA-line maxi skirts in cotton or denim — easy everyday wear with sneakers or sandals.\n\nKnit maxi skirts (ribbed or sweater-knit) — autumn and winter staples that pair with everything.\n\nDenim maxi skirts — a casual modest alternative to jeans.",
      },
      {
        heading: "How to style modest skirts",
        body:
          "Pleated satin maxi + cropped sweater + neutral hijab = polished evening look.\n\nDenim maxi + tucked tee + sneakers = modest casual everyday.\n\nKnit maxi + oversized blazer + ankle boots = elevated modest workwear.",
      },
      {
        heading: "Skirt length & coverage",
        body:
          "Look for skirts that hit at or below the ankle. Avoid front or thigh-high slits — back kick pleats are fine and aid walking.\n\nLined skirts are essential for satin and chiffon to maintain opacity.",
      },
    ],
    faqs: [
      {
        q: "What is a modest skirt?",
        a: "A modest skirt is a maxi or midi skirt that covers to the ankle or floor with opaque fabric and no high slits.",
      },
      {
        q: "Are pleated maxi skirts modest?",
        a: "Yes — when they're lined, ankle or floor length, and worn with a long top or tucked-in long-sleeve.",
      },
      {
        q: "Can I wear a denim maxi skirt to work?",
        a: "In casual workplaces, yes — pair with a structured blazer and silk blouse for a smart-casual modest look.",
      },
      {
        q: "How long should a modest skirt be?",
        a: "Modest skirts typically end at the ankle or below, providing full leg coverage even when sitting or walking.",
      },
      {
        q: "What do I wear under a sheer modest skirt?",
        a: "Always pair sheer satin or chiffon skirts with a matching opaque slip or built-in lining to maintain full coverage.",
      },
    ],
    related: [
      { label: "Modest tops & tunics", href: "/modest-tops-tunics" },
      { label: "Modest workwear", href: "/modest-workwear" },
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "Hijabs", href: "/hijabs" },
    ],
  },

  // 10. JILBABS & KHIMARS
  {
    slug: "jilbabs-khimars",
    primaryKeyword: "jilbabs and khimars",
    title: "Jilbabs & Khimars (Two-Piece Modest Sets) | HijabiStore",
    metaDescription:
      "Shop jilbabs and khimars — full-coverage Islamic garments for prayer and daily wear. Free US shipping on $75+.",
    h1: "Jilbabs & Khimars — Two-Piece Modest Sets",
    heroSubtitle:
      "Two-piece modest sets — khimar + skirt or full jilbab styles — for full coverage, easy wear, and the most relaxed modest silhouette.",
    heroImageAlt: "Black khimar and matching skirt two-piece modest set",
    categorySlugs: ["jilbabs", "khimars", "khimar", "jilbab"],
    productKeywords: ["jilbab", "khimar", "two-piece", "set"],
    intro:
      "Jilbabs and khimars offer the most relaxed, full-coverage modest silhouette — perfect for daily wear, school, and sisters who prefer wider, looser cuts. A khimar covers the head and chest in one drape; pair it with a maxi skirt or wide trousers for a complete two-piece outfit.",
    sections: [
      {
        heading: "Khimar styles",
        body:
          "Half-khimar (waist-length): everyday wear, easy to layer.\n\nThree-quarter khimar (knee-length): classic and most popular length.\n\nFull khimar (ankle-length): worn over a skirt for maximum coverage.\n\nLayered khimars with a separate inner cap give extra fullness around the face.",
      },
      {
        heading: "Jilbab cuts",
        body:
          "One-piece jilbab: full garment with attached headcover.\n\nTwo-piece jilbab set: matching khimar + skirt — easy to wash and to mix sizes.\n\nButterfly jilbab: wide draping at the sleeves and chest for a fuller silhouette.",
      },
      {
        heading: "Best fabrics for jilbabs and khimars",
        body:
          "Chiffon jilbabs (with lined skirt) for warm weather and events.\n\nNida and crepe for everyday durability and clean drape.\n\nJersey and modal for soft, comfortable daily wear.",
      },
    ],
    faqs: [
      {
        q: "What is a khimar?",
        a: "A khimar is a long head covering that drapes over the head, shoulders, and chest in one piece — typically waist, knee, or ankle length.",
      },
      {
        q: "What's the difference between a jilbab and an abaya?",
        a: "An abaya is a loose outer robe worn over clothing. A jilbab traditionally refers to a longer, often two-piece garment that may include a head covering.",
      },
      {
        q: "Are jilbabs and khimars only black?",
        a: "No — they come in every color. Soft neutrals and earth tones are increasingly popular alongside classic black.",
      },
      {
        q: "Can I wear a khimar to work?",
        a: "Yes — neutral-colored khimars in modal or crepe with matching skirts read polished and professional.",
      },
      {
        q: "How do I wash jilbabs and khimars?",
        a: "Most chiffon and crepe pieces are hand wash cold; jersey and modal can be machine washed on a delicate cycle. Always lay flat to dry.",
      },
    ],
    related: [
      { label: "Abayas", href: "/abayas" },
      { label: "Prayer dresses", href: "/prayer-dresses" },
      { label: "Hijabs", href: "/hijabs" },
      { label: "Modest skirts", href: "/modest-skirts" },
    ],
  },

  // 11. KAFTANS
  {
    slug: "kaftans",
    primaryKeyword: "kaftans",
    title: "Kaftans & Moroccan Dresses for Women | HijabiStore",
    metaDescription:
      "Shop kaftans — flowing, elegant kaftan dresses for casual wear and special occasions. Free US shipping on orders over $75.",
    h1: "Kaftans & Moroccan Dresses — Embroidered, Beaded & Lounge",
    heroSubtitle:
      "Flowing kaftans for Eid, weddings, and weekends in. From hand-embroidered Moroccan kaftans to soft cotton lounge styles.",
    heroImageAlt: "Embroidered Moroccan kaftan in jewel tones with detailed neckline beading",
    categorySlugs: ["kaftans", "kaftan", "moroccan-dresses"],
    productKeywords: ["kaftan", "caftan", "moroccan", "takchita"],
    intro:
      "Kaftans are the easiest modest statement piece — full-length, free-flowing, and effortlessly elegant. We carry hand-embroidered Moroccan kaftans for weddings and Eid, simpler beaded kaftans for evening events, and soft cotton kaftans cut for lounging at home.",
    sections: [
      {
        heading: "Kaftan styles for every occasion",
        body:
          "Moroccan kaftan / takchita: ornately embroidered, often two-layered with a sashed waist — for weddings, formal Eid events, and milestone celebrations.\n\nEvening kaftan: simpler beaded or embroidered details, single-layered — perfect for dinners and engagements.\n\nLounge kaftan: cotton, linen, or modal in solid colors — for relaxed at-home wear.",
      },
      {
        heading: "How to style a kaftan",
        body:
          "Cinch a flowing kaftan with a slim metallic belt for a defined waist.\n\nLayer with statement earrings; let the kaftan be the focal point.\n\nFor formal looks, pair with a coordinating silk hijab and heeled mules.",
      },
      {
        heading: "Caring for embroidered kaftans",
        body:
          "Beaded and embroidered kaftans should always be dry cleaned to preserve detailing.\n\nSimple cotton lounge kaftans can be machine washed cold and hung to dry.",
      },
    ],
    faqs: [
      {
        q: "What is a kaftan?",
        a: "A kaftan is a long, loose-fitting tunic-style dress with origins in North African and Middle Eastern cultures, often embroidered or embellished for formal occasions.",
      },
      {
        q: "What's the difference between a kaftan and an abaya?",
        a: "Kaftans tend to have wider sleeves and richer embellishment; abayas are typically simpler and used as everyday outerwear.",
      },
      {
        q: "Can I wear a kaftan to a wedding?",
        a: "Yes — embroidered Moroccan kaftans (takchitas) are a traditional and elegant choice for weddings and formal events.",
      },
      {
        q: "How do I wash an embroidered kaftan?",
        a: "Always dry clean embroidered or beaded kaftans to protect the detailing.",
      },
      {
        q: "Are kaftans suitable for hijab wearers?",
        a: "Yes — most kaftans have high necklines and long sleeves. Pair with a tonal silk hijab for a polished modest look.",
      },
    ],
    related: [
      { label: "Abayas", href: "/abayas" },
      { label: "Modest evening wear", href: "/modest-eveningwear" },
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "Hijabs", href: "/hijabs" },
    ],
  },

  // 12. UNDERSCARVES & HIJAB CAPS
  {
    slug: "underscarves-hijab-caps",
    primaryKeyword: "underscarves and hijab caps",
    title: "Underscarves & Hijab Caps — No-Slip Hijabs | HijabiStore",
    metaDescription:
      "Shop underscarves & hijab caps — jersey caps, ninja undercaps, tie-back bonnets & volumizers. Stop your hijab from slipping. Free US shipping over $75.",
    h1: "Underscarves & Hijab Caps",
    heroSubtitle:
      "The unsung heroes of hijab styling — jersey caps, ninja undercaps, tie-back bonnets, and volumizers that keep every hijab in place.",
    heroImageAlt: "Set of jersey hijab caps in neutral colors",
    categorySlugs: ["underscarves", "hijab-caps", "hijab-undercaps"],
    productKeywords: ["underscarf", "undercap", "hijab cap", "bonnet", "volumizer"],
    intro:
      "If your hijab keeps slipping, the issue is almost always the foundation underneath. Underscarves and hijab caps grip your hair, hide flyaways, and give silk and chiffon hijabs something to hold onto. This collection includes jersey tube caps, ninja undercaps with neck coverage, tie-back bonnets, and volumizers for an elevated silhouette.",
    sections: [
      {
        heading: "Hijab cap types",
        body:
          "Jersey tube caps: stretchy slip-on caps that cover the hairline — the everyday workhorse.\n\nNinja undercaps: tube cap + neck cover in one piece — full coverage in a single layer.\n\nTie-back bonnets: secure with a ribbon for a custom fit.\n\nVolumizers / bumps: padded inserts that add height and shape under the hijab.",
      },
      {
        heading: "How to use an underscarf",
        body:
          "Pull the cap down to cover your hairline and ears. Make sure no hair peeks out at the front.\n\nWrap your hijab over the cap as usual — silk and chiffon will grip the cap fabric instead of your hair.\n\nSecure with one or two pins (or magnetic clips) at the side.",
      },
      {
        heading: "Best fabrics for underscarves",
        body:
          "Cotton-jersey blends are breathable for daily wear.\n\nBamboo and modal are gentle on sensitive scalps.\n\nSatin-lined caps protect hair from breakage and frizz — ideal for curly and textured hair.",
      },
    ],
    faqs: [
      {
        q: "What is an underscarf?",
        a: "An underscarf is a snug cap or tube worn under a hijab to cover the hairline, hide hair, and give silk or chiffon hijabs something to grip.",
      },
      {
        q: "Why does my hijab keep slipping?",
        a: "Usually because there's no underscarf underneath. Silk and chiffon especially need a jersey or cotton cap base to grip onto.",
      },
      {
        q: "What's a ninja undercap?",
        a: "A ninja undercap is a one-piece tube that covers the head and neck in a single layer — popular for full coverage under a single hijab wrap.",
      },
      {
        q: "Are satin-lined hijab caps better for hair?",
        a: "Yes — satin reduces friction, breakage, and frizz, especially for curly and textured hair.",
      },
      {
        q: "How many underscarves do I need?",
        a: "Three to five caps in colors matching your most-worn hijabs (typically black, white, beige, and one accent) cover daily styling needs.",
      },
    ],
    related: [
      { label: "Hijabs", href: "/hijabs" },
      { label: "Hijab style quiz", href: "/quiz" },
      { label: "Modest activewear", href: "/modest-activewear" },
      { label: "Abayas", href: "/abayas" },
    ],
  },

  // 13. MODEST EVENINGWEAR
  {
    slug: "modest-eveningwear",
    primaryKeyword: "modest evening wear",
    title: "Modest Evening Wear & Formal Dresses | HijabiStore",
    metaDescription:
      "Shop modest evening dresses and formal gowns. Elegant full-coverage eveningwear for weddings, galas & special occasions. Free US shipping on $75+.",
    h1: "Modest Evening Wear & Formal Dresses",
    heroSubtitle:
      "Long-sleeve gowns, embellished maxi dresses, and formal abayas — modest evening wear for weddings, Eid, galas, and milestone celebrations.",
    heroImageAlt: "Floor-length modest evening gown with long sleeves and beaded detail",
    categorySlugs: ["modest-eveningwear", "evening-wear", "formal", "gowns"],
    productKeywords: ["evening", "gown", "formal", "wedding", "gala", "embellished"],
    intro:
      "Modest evening wear has finally caught up with what hijabi women have wanted for years: long sleeves, full-length hems, opaque luxe fabrics, and silhouettes that feel current. From beaded gowns and silk maxi dresses to formal embroidered abayas and Moroccan kaftans, this is your destination for modest formal events.",
    sections: [
      {
        heading: "Modest evening wear styles",
        body:
          "Long-sleeve evening gowns: floor-length silhouettes in satin, silk, and crepe with covered shoulders.\n\nEmbellished maxi dresses: beadwork, sequins, or embroidery on opaque base fabric — Eid- and gala-ready.\n\nFormal abayas: tailored open-front or butterfly cuts with subtle embroidery.\n\nMoroccan kaftans (takchitas): two-layered, ornately embellished, the most traditional modest formal wear.",
      },
      {
        heading: "Choosing modest evening wear by event",
        body:
          "Wedding guest: a beaded long-sleeve gown or a richly embroidered abaya in a complementary tone.\n\nEid celebrations: silk or satin maxi dresses, embellished kaftans, or formal butterfly abayas.\n\nGala or charity event: a structured long-sleeve gown in a classic color (navy, emerald, burgundy, black).\n\nEngagement / nikah: cream, blush, or ivory gowns with hand embroidery.",
      },
      {
        heading: "Hijab pairings for modest evening wear",
        body:
          "Silk and satin hijabs in tonal or jewel-tone shades elevate any evening look.\n\nFor heavily embellished outfits, choose a solid hijab in a complementary color rather than competing patterns.",
      },
    ],
    faqs: [
      {
        q: "Where can I find modest evening dresses?",
        a: "Right here — our modest eveningwear collection includes long-sleeve gowns, embellished abayas, and Moroccan kaftans from independent designers.",
      },
      {
        q: "What's the best modest dress for a wedding guest?",
        a: "A floor-length, long-sleeve gown in satin or silk, or a richly embroidered abaya in a complementary tone to the wedding palette.",
      },
      {
        q: "Are modest evening dresses available in plus sizes?",
        a: "Yes — many of our vendors stock formal modest pieces through US 24+ with extended length options.",
      },
      {
        q: "How should a hijabi accessorize an evening look?",
        a: "Choose a silk or satin hijab in a tonal or complementary color, statement earrings (pinned to the hijab), and a clutch — let one element be the focal point.",
      },
      {
        q: "Can I wear color to a wedding as a guest?",
        a: "Yes — jewel tones (emerald, sapphire, plum), warm neutrals, and dusty pastels all photograph beautifully. Avoid white, ivory, and champagne.",
      },
    ],
    related: [
      { label: "Kaftans", href: "/kaftans" },
      { label: "Abayas", href: "/abayas" },
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "Hijabs", href: "/hijabs" },
    ],
  },

  // 14. CHIFFON HIJABS
  {
    slug: "chiffon-hijabs",
    primaryKeyword: "chiffon hijabs",
    title: "Chiffon Hijabs — Lightweight & Breathable | HijabiStore",
    metaDescription:
      "Shop chiffon hijabs — lightweight, elegant hijabs for formal & everyday styling. Beautiful drape in 50+ colors. Free US shipping on $75+.",
    h1: "Chiffon Hijabs — Lightweight, Breathable, Elegant",
    heroSubtitle: "Airy chiffon hijabs in solids, prints, and ombré finishes — the go-to choice for warm weather, weddings, and dressy occasions.",
    heroImageAlt: "Folded chiffon hijabs in soft pastel and neutral shades",
    categorySlugs: ["chiffon", "hijabs"],
    productKeywords: ["chiffon hijab", "chiffon scarf"],
    intro:
      "Chiffon is the most-loved hijab fabric for a reason: it's feather-light, drapes beautifully, and photographs like a dream. We curate chiffon hijabs from independent vendors so you can find your shade and shine without sacrificing breathability or comfort.",
    sections: [
      {
        heading: "Why chiffon hijabs?",
        body:
          "Chiffon is woven from twisted yarns that give it a slight crepe texture and natural drape. The fabric breathes well in heat, layers smoothly under coats in winter, and falls in elegant folds for any styling.\n\nBecause chiffon is semi-sheer, an underscarf or jersey cap underneath gives full opacity and helps the hijab grip without sliding.",
      },
      {
        heading: "Best occasions for chiffon hijabs",
        body:
          "Weddings and engagements: chiffon catches light and pairs perfectly with dressier abayas and gowns.\n\nEid and celebrations: choose pastel or jewel-tone chiffon to match festive outfits.\n\nSummer days: lightweight chiffon stays cool even in humid weather.\n\nOffice and events: a solid chiffon in navy, taupe, or ivory looks polished without being heavy.",
      },
      {
        heading: "How to style chiffon hijabs",
        body:
          "Always start with an underscarf cap to anchor the chiffon and prevent slipping.\n\nUse no-snag hijab pins or magnetic clips — they hold without leaving holes.\n\nFor wrapped styles, pin near the temple and let the longer side drape over the shoulder for clean lines.",
      },
    ],
    faqs: [
      { q: "Are chiffon hijabs see-through?", a: "Lightly, yes — that's why we always recommend a matching jersey underscarf or cap underneath for full opacity." },
      { q: "Can I wear chiffon hijabs daily?", a: "Absolutely. Choose double-layered or premium chiffon for everyday durability and pair with a non-slip undercap." },
      { q: "How do I wash a chiffon hijab?", a: "Hand wash in cool water with mild detergent, never wring, and lay flat to dry away from direct sunlight." },
      { q: "Will chiffon hijabs slip?", a: "Only without an undercap. With a jersey or cotton underscarf, chiffon stays put all day." },
      { q: "What length chiffon hijab is best?", a: "Standard rectangle (around 27\" x 70\") is the most versatile. Maxi chiffon (75\"+ long) gives extra drape for events and chest coverage." },
    ],
    related: [
      { label: "Jersey hijabs", href: "/jersey-hijabs" },
      { label: "Silk hijabs", href: "/silk-hijabs" },
      { label: "Underscarves & caps", href: "/underscarves-hijab-caps" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // 15. JERSEY HIJABS
  {
    slug: "jersey-hijabs",
    primaryKeyword: "jersey hijabs",
    title: "Jersey Hijabs: No-Slip, No-Pin Everyday Hijabs | HijabiStore",
    metaDescription:
      "Shop jersey hijabs — soft, stretchy, no-pin hijabs for everyday wear. Beginner-friendly hijabs in 50+ colors. Free US shipping on $75+.",
    h1: "Jersey Hijabs — Soft, Stretchy, No-Slip",
    heroSubtitle: "The everyday hijab that grips naturally — no pins, no fuss. Perfect for beginners, busy days, and active lifestyles.",
    heroImageAlt: "Stack of folded jersey hijabs in muted everyday colors",
    categorySlugs: ["jersey", "hijabs"],
    productKeywords: ["jersey hijab"],
    intro:
      "Jersey hijabs are the workhorse of any hijab wardrobe. The fabric's natural stretch and grip mean you can throw one on, wrap it once, and walk out the door. We stock jersey hijabs in maxi lengths, premium weights, and a full color palette from independent modest brands.",
    sections: [
      {
        heading: "Why beginners choose jersey",
        body:
          "Jersey grips itself and the head, so you don't need pins to keep it in place. New hijabis can wrap, tuck, and go — no styling expertise required.\n\nThe fabric also forgives mistakes: re-wrap mid-day without re-pinning, and the look stays clean.",
      },
      {
        heading: "Premium vs basic jersey",
        body:
          "Premium jersey is heavier (220–280 gsm), drapes cleanly, and resists pilling.\n\nBasic jersey is lighter and more affordable but can pull or thin over time.\n\nFor everyday wear, invest in 2–3 premium jerseys in your most-worn colors.",
      },
      {
        heading: "Maxi jersey for full coverage",
        body:
          "Maxi jersey hijabs (75\"+ long) drape down past the chest and allow looser, fuller wraps.\n\nIdeal for prayer, formal looks, and anyone who prefers extra coverage.",
      },
    ],
    faqs: [
      { q: "Do I need pins with jersey hijabs?", a: "No. Jersey grips naturally — most wearers use zero pins. A single magnetic clip is optional for high-movement days." },
      { q: "Are jersey hijabs hot in summer?", a: "Premium jersey breathes well, but for very hot days, a modal or cotton hijab will feel cooler." },
      { q: "Can I sleep in a jersey hijab?", a: "Many wearers do — the soft, stretchy fabric is comfortable enough for lounging and overnight wear." },
      { q: "How do I wash jersey hijabs?", a: "Machine wash cold on delicate cycle, hang or tumble dry low. They hold shape for years with proper care." },
      { q: "Are jersey hijabs good for beginners?", a: "They're the #1 recommendation for beginners — no pins, no styling skill needed, and they don't slip." },
    ],
    related: [
      { label: "Chiffon hijabs", href: "/chiffon-hijabs" },
      { label: "Sports hijabs", href: "/sports-hijabs-shop" },
      { label: "Underscarves & caps", href: "/underscarves-hijab-caps" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // 16. SILK & SATIN HIJABS
  {
    slug: "silk-hijabs",
    primaryKeyword: "silk hijabs",
    title: "Silk & Satin Hijabs — Luxury Hijabs | HijabiStore",
    metaDescription:
      "Shop silk hijabs — luxurious, smooth silk hijabs for special occasions and everyday elegance. Premium feel and drape. Free US shipping on $75+.",
    h1: "Silk & Satin Hijabs — Luxe Shine for Special Days",
    heroSubtitle: "Real silk and high-shine satin hijabs that elevate any outfit — designed for weddings, Eid, and statement evenings.",
    heroImageAlt: "Glossy silk and satin hijabs draped in jewel tones",
    categorySlugs: ["silk-satin", "hijabs"],
    productKeywords: ["silk hijab", "satin hijab"],
    intro:
      "When the occasion calls for something more, silk and satin deliver. We curate real silk and premium satin hijabs from independent vendors — the kind of pieces you save for weddings, Eid, and the events you want to remember.",
    sections: [
      {
        heading: "Real silk vs satin: what's the difference?",
        body:
          "Real silk is a natural protein fiber — breathable, temperature-regulating, and luxurious to the touch. Hand wash only.\n\nSatin refers to a weave (not a fiber), often made from polyester or silk. It's machine-washable when polyester-based and offers similar shine at a lower price point.",
      },
      {
        heading: "How to style silk and satin hijabs",
        body:
          "Always wear a non-slip underscarf — silk is slippery by nature.\n\nUse silk-friendly hijab pins (no sharp tips) or magnetic clips to avoid snags.\n\nKeep the wrap simple — let the fabric's shine be the statement.",
      },
      {
        heading: "Caring for silk hijabs",
        body:
          "Hand wash in cool water with a silk-safe detergent.\n\nNever wring — gently press water out with a towel.\n\nLay flat or hang to dry away from direct sunlight.\n\nIron on the lowest setting through a cloth if needed.",
      },
    ],
    faqs: [
      { q: "Are silk hijabs worth the price?", a: "Yes — real silk lasts decades with proper care, regulates temperature better than synthetics, and looks unmistakably luxurious." },
      { q: "Can I wear silk hijabs daily?", a: "You can, but most people save silk for special occasions due to delicate care requirements." },
      { q: "Do silk hijabs slip a lot?", a: "Yes — always wear an underscarf and use snag-free pins or magnetic clips." },
      { q: "Is satin a good silk alternative?", a: "Polyester satin offers similar shine, easier care, and a lower price — a great option for frequent wear." },
      { q: "How do I store silk hijabs?", a: "Roll loosely or hang on a padded hanger in a cool, dark space. Avoid plastic bags which can trap moisture." },
    ],
    related: [
      { label: "Chiffon hijabs", href: "/chiffon-hijabs" },
      { label: "Modest eveningwear", href: "/modest-eveningwear" },
      { label: "Hijab pins & magnets", href: "/hijab-pins-magnets-shop" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // 17. MAGNETIC HIJABS
  {
    slug: "magnetic-hijabs",
    primaryKeyword: "magnetic hijabs",
    title: "Magnetic Hijabs: No-Pin, No-Snag Hijab Styling | HijabiStore",
    metaDescription:
      "Shop magnetic hijabs — innovative no-pin hijabs with built-in magnets for secure, effortless wear all day. Free US shipping on $75+.",
    h1: "Magnetic Hijabs — Pin-Free, Snag-Free Styling",
    heroSubtitle: "Style any hijab with strong, fabric-safe magnets — no holes, no snags, no fuss.",
    heroImageAlt: "Magnetic hijab pins and clips arranged on a soft fabric",
    categorySlugs: ["magnetic-pins", "hijabs"],
    productKeywords: ["magnetic hijab", "magnetic pin", "magnet"],
    intro:
      "Magnetic hijab pins replaced regular pins for thousands of hijabis — and for good reason. They hold strong, leave no marks, and work on every fabric from delicate silk to thick jersey. Shop magnetic hijabs and magnetic pin sets from vetted vendors.",
    sections: [
      {
        heading: "How magnetic hijab pins work",
        body:
          "A magnetic pin uses two strong rare-earth magnets — one on each side of the fabric — to grip without piercing.\n\nThis means no holes in delicate chiffon or silk, and no snags on jersey or modal.",
      },
      {
        heading: "Best fabrics for magnetic styling",
        body:
          "Chiffon, silk, and satin — magnets save delicate weaves from pinholes.\n\nJersey and modal — magnets prevent runs and pulls.\n\nHeavy hijabs (over 280 gsm) need stronger or double magnets to hold firmly.",
      },
      {
        heading: "Tips for using magnetic pins",
        body:
          "Keep magnetic pins away from credit cards, watches, and electronic devices.\n\nStore in a small magnetic case to prevent loss.\n\nIf the hold weakens, replace the magnet — they last 1–2 years with daily use.",
      },
    ],
    faqs: [
      { q: "Are magnetic hijab pins safe?", a: "Yes — they use sealed neodymium magnets that are safe on all fabrics. Just keep them away from cards and pacemakers." },
      { q: "Will magnetic pins damage my hijab?", a: "No. They grip without piercing, making them safer than traditional pins for delicate fabrics." },
      { q: "Do magnetic pins work on thick hijabs?", a: "Yes, but for very heavy hijabs you may want stronger or doubled magnets." },
      { q: "How many magnetic pins do I need?", a: "A starter set of 4–6 covers most styles — keep extras in your bag, purse, and at home." },
      { q: "Can I fly with magnetic hijab pins?", a: "Yes — they pass through airport security without issue. Pack them in your carry-on for easy hijab adjustments mid-flight." },
    ],
    related: [
      { label: "Hijab pins & magnets", href: "/hijab-pins-magnets-shop" },
      { label: "Chiffon hijabs", href: "/chiffon-hijabs" },
      { label: "Silk hijabs", href: "/silk-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // 18. INSTANT / PRE-TIED HIJABS
  {
    slug: "instant-hijabs",
    primaryKeyword: "instant hijabs",
    title: "Instant & Pre-Tied Hijabs — Slip-On Ready | HijabiStore",
    metaDescription:
      "Shop instant hijabs — ready-to-wear, no-pin hijabs for quick and easy styling. Perfect for busy days. Free US shipping on $75+.",
    h1: "Instant & Pre-Tied Hijabs — Slip On, Walk Out",
    heroSubtitle: "Sewn-shape hijabs that style themselves — ideal for travel, school runs, work, and anyone short on time.",
    heroImageAlt: "Pre-tied instant hijabs in soft solid colors",
    categorySlugs: ["instant-pre-tied-hijabs", "hijabs"],
    productKeywords: ["instant hijab", "pre-tied hijab", "slip on hijab"],
    intro:
      "Instant hijabs (also called pre-tied or slip-on hijabs) come pre-shaped — you pull them over your head and you're done. No pins, no wraps, no styling. We curate instant hijabs from independent brands in jersey, chiffon, and bamboo blends.",
    sections: [
      {
        heading: "Who instant hijabs are for",
        body:
          "Travelers — slip on at the airport without worrying about pins or wraps.\n\nStudents and busy parents — five-second hijab on hectic mornings.\n\nNew hijabis learning the basics — wear an instant while you practice traditional styles.",
      },
      {
        heading: "Instant hijab styles",
        body:
          "Al-Amira: two-piece set with a fitted underscarf and a slip-on hood.\n\nOne-piece tube: pull straight over the head, adjust at the temple.\n\nNinja-style instant: cap-and-cover combo for full coverage in one motion.",
      },
      {
        heading: "Caring for instant hijabs",
        body:
          "Most jersey and modal instant hijabs are machine washable on a cold delicate cycle — hang to dry to preserve the sewn shape.\n\nChiffon instants should be hand washed and laid flat.\n\nStore folded rather than hung to keep the pre-tied silhouette intact.\n\nAvoid high-heat ironing; steam lightly if needed.",
      },
    ],
    faqs: [
      { q: "Are instant hijabs comfortable?", a: "Most are made from breathable jersey, modal, or bamboo — soft enough for all-day wear." },
      { q: "Can I style an instant hijab?", a: "Yes — many allow simple draping or twisting to vary the look. They're not as flexible as wrapped hijabs but much faster." },
      { q: "Do instant hijabs come in different lengths?", a: "Yes — from short tube styles to maxi instants that drape past the chest." },
      { q: "Are they good for sports?", a: "Yes — many sports hijabs are instant pre-tied designs in moisture-wicking fabric." },
      { q: "Do instant hijabs need an underscarf?", a: "Most don't — the sewn cap section grips the hairline. For chiffon instants you may want a thin jersey cap underneath for full opacity." },
    ],
    related: [
      { label: "Sports hijabs", href: "/sports-hijabs-shop" },
      { label: "Underscarves & caps", href: "/underscarves-hijab-caps" },
      { label: "Jersey hijabs", href: "/jersey-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // 19. SPORTS HIJABS
  {
    slug: "sports-hijabs-shop",
    primaryKeyword: "sports hijabs",
    title: "Sports Hijabs — Breathable Athletic Hijabs | HijabiStore",
    metaDescription:
      "Shop sports hijabs — breathable, sweat-wicking workout hijabs for running, gym & athletics. Stay covered and comfortable. Free US shipping on $75+.",
    h1: "Sports Hijabs — Moisture-Wicking, Secure, Athletic",
    heroSubtitle: "Performance hijabs designed for movement — breathable fabrics, snug fit, and zero slip during workouts.",
    heroImageAlt: "Athletic sports hijabs in performance fabrics",
    categorySlugs: ["sports-hijabs", "sport-hijabs", "modest-activewear"],
    productKeywords: ["sport hijab", "sports hijab", "athletic hijab"],
    intro:
      "A great sports hijab keeps you cool, dry, and focused — whether you're running, lifting, or playing team sports. Shop performance hijabs in moisture-wicking fabrics from modest activewear vendors.",
    sections: [
      {
        heading: "What makes a great sports hijab",
        body:
          "Moisture-wicking fabric (polyester blends, bamboo) pulls sweat away from skin.\n\nFour-way stretch ensures no riding up during burpees, jumps, or twists.\n\nFlat seams prevent rubbing on the forehead, ears, and neck during long sessions.\n\nLightweight construction keeps you cool indoors and outdoors.",
      },
      {
        heading: "Pre-tied vs wrap sports hijabs",
        body:
          "Pre-tied sports hijabs slip on in one motion — best for high-intensity workouts.\n\nWrap-style sports hijabs use longer fabric and built-in grips — better for full coverage during long runs or hot yoga.",
      },
      {
        heading: "Caring for sports hijabs",
        body:
          "Wash after every workout in cold water on a delicate cycle — sweat breaks down performance fabrics over time.\n\nAvoid fabric softener; it coats moisture-wicking fibers and reduces breathability.\n\nAir dry whenever possible — high heat damages elastane and shortens hijab life.\n\nRotate two or three sports hijabs to extend the life of each.",
      },
    ],
    faqs: [
      { q: "Can I swim in a sports hijab?", a: "Some sports hijabs are chlorine-resistant and double as swim hijabs — check the listing for swim-rated fabric." },
      { q: "Will a sports hijab stay during burpees?", a: "Yes — properly fitted pre-tied sports hijabs stay in place through full ranges of motion." },
      { q: "Are sports hijabs hot?", a: "Performance fabrics are designed to feel cool and dry even during sweaty workouts." },
      { q: "What size sports hijab do I need?", a: "Most use S/M/L sizing — check the brand's measurement guide. Snug-fit pre-tied styles need accurate sizing." },
      { q: "Can I wear a sports hijab as a regular daily hijab?", a: "Yes — many wearers love them for travel, school runs, and any active day. The moisture-wicking fabric stays cool all day." },
    ],
    related: [
      { label: "Modest activewear", href: "/modest-activewear" },
      { label: "Modest swimwear", href: "/modest-swimwear" },
      { label: "Instant hijabs", href: "/instant-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // 20. HIJAB PINS & MAGNETS
  {
    slug: "hijab-pins-magnets-shop",
    primaryKeyword: "hijab pins and magnets",
    title: "Hijab Pins & Magnets — Snag-Free Hold | HijabiStore",
    metaDescription:
      "Shop hijab pins and magnets — no-snag magnetic pins, decorative brooches & safety pins for secure hijab styling. Free US shipping on $75+.",
    h1: "Hijab Pins & Magnets — Snag-Free, Strong-Hold",
    heroSubtitle: "From discreet straight pins to decorative magnetic clips — every pin you need to style with confidence.",
    heroImageAlt: "Hijab pins and magnetic clips arranged on neutral background",
    categorySlugs: ["hijab-pins", "hijab-pins-magnets", "hijab-essentials"],
    productKeywords: ["hijab pin", "magnetic clip", "hijab brooch"],
    intro:
      "The right pin makes the difference between a hijab that holds all day and one you keep adjusting. We curate snag-free straight pins, strong magnetic clips, and decorative brooches from independent modest accessory brands.",
    sections: [
      {
        heading: "Types of hijab pins",
        body:
          "Straight pins: thin, discreet, ideal for chiffon and silk.\n\nMagnetic pins: pierce-free, perfect for delicate fabrics.\n\nSafety-style hijab pins: combine pin and clasp — secure for active days.\n\nDecorative brooches: function as a pin and a statement accessory.",
      },
      {
        heading: "Choosing the right pin for your hijab",
        body:
          "Light, slippery hijabs (chiffon, silk, satin): use magnetic pins or fine straight pins.\n\nMedium hijabs (modal, cotton, jersey): straight pins or safety pins work well.\n\nHeavy hijabs and shaylas: stronger magnetic clips or layered pins for hold.",
      },
      {
        heading: "Storing and caring for hijab pins",
        body:
          "Keep magnetic pins in a small lined case to prevent them from sticking to keys, cards, or electronics.\n\nWipe straight pins clean after use to prevent rust on humid days.\n\nReplace bent pins immediately — even small bends can snag delicate fabrics.\n\nRotate decorative brooches based on hijab color to extend their visual life.",
      },
    ],
    faqs: [
      { q: "Are hijab pins safe to fly with?", a: "Yes — they're considered standard pins. Magnetic pins are also safe through airport security." },
      { q: "How many hijab pins do I need?", a: "A starter set of 6–10 pins covers most styles. Keep extras in your bag for emergencies." },
      { q: "Do magnetic pins really hold?", a: "Quality neodymium magnets hold even heavy chiffon for hours. Replace yearly if grip weakens." },
      { q: "Can I use brooches as hijab pins?", a: "Yes — many brooches double as decorative hijab pins, adding sparkle to plain hijabs." },
      { q: "Will hijab pins damage delicate fabrics?", a: "Fine straight pins and magnetic clips are safest for chiffon and silk. Avoid thick safety pins on lightweight fabrics." },
    ],
    related: [
      { label: "Magnetic hijabs", href: "/magnetic-hijabs" },
      { label: "Underscarves & caps", href: "/underscarves-hijab-caps" },
      { label: "Chiffon hijabs", href: "/chiffon-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // 21. OPEN-FRONT ABAYAS
  {
    slug: "open-front-abayas",
    primaryKeyword: "open-front abayas",
    title: "Open-Front Abayas: Layering Abayas & Dusters | HijabiStore",
    metaDescription:
      "Shop open front abayas — stylish, layering-friendly open abayas in premium fabrics. Free US shipping on orders over $75.",
    h1: "Open-Front Abayas — Layer, Style, Repeat",
    heroSubtitle: "The most versatile abaya silhouette — wear over jeans, dresses, or workwear and instantly elevate any look.",
    heroImageAlt: "Open-front abayas hanging in muted neutral palette",
    categorySlugs: ["open-abayas", "cl-open-abayas", "abayas-style"],
    productKeywords: ["open abaya", "abaya duster", "open front abaya"],
    intro:
      "Open-front abayas are the modern hijabi's secret weapon — they work over jeans on a coffee run, over a dress for a wedding guest look, and over workwear for instant polish. We curate open abayas from independent vendors in everyday and formal weights.",
    sections: [
      {
        heading: "Why open abayas are essential",
        body:
          "They layer over any outfit — instant modest styling without changing what's underneath.\n\nThey transition seamlessly from day to night.\n\nThey travel well — pack one open abaya and dress up multiple outfits.",
      },
      {
        heading: "Open abaya styles",
        body:
          "Duster abayas: lightweight, long, often in linen or modal.\n\nKimono open abayas: wider sleeves, often belted at the waist.\n\nFormal open abayas: embroidered, beaded, or in luxe satin and crepe for events.",
      },
      {
        heading: "How to style open-front abayas",
        body:
          "Belt at the waist with a slim leather or fabric belt to define your silhouette.\n\nLayer over a fitted maxi dress or tunic-and-pant combo for a clean line.\n\nMatch your hijab in tonal silk or chiffon — let the abaya texture be the focal point.\n\nFor casual looks, throw an open abaya over a long-sleeve tee and jeans for instant modest polish.",
      },
    ],
    faqs: [
      { q: "Can I wear an open abaya without buttons?", a: "Yes — many are designed to drape open. Use a discreet snap or belt if you prefer some closure." },
      { q: "What do you wear under an open abaya?", a: "Anything modest — long dresses, tunics with pants, or even a top and skirt combination." },
      { q: "Are open abayas formal enough for weddings?", a: "Embroidered or satin open abayas are absolutely wedding-appropriate, especially over a long dress." },
      { q: "Do open abayas work for tall women?", a: "Yes — many vendors offer abayas in 58\"–62\" lengths specifically for tall hijabis." },
      { q: "How do I wash an open-front abaya?", a: "Most crepe and nida open abayas are machine washable cold; embellished pieces should be dry cleaned." },
    ],
    related: [
      { label: "Closed abayas", href: "/closed-abayas-shop" },
      { label: "Kimono abayas", href: "/kimono-abayas-shop" },
      { label: "Modest cardigans & dusters", href: "/modest-cardigans-dusters" },
      { label: "All abayas", href: "/abayas" },
    ],
  },

  // 22. CLOSED ABAYAS
  {
    slug: "closed-abayas-shop",
    primaryKeyword: "closed abayas",
    title: "Closed Abayas — Pull-Over Modest Abayas | HijabiStore",
    metaDescription:
      "Shop closed abayas — elegant, fully covered abayas for daily wear and special occasions. Free US shipping on $75+.",
    h1: "Closed Abayas — Full Coverage, Pull-On Ease",
    heroSubtitle: "Pull-over abayas in everyday and formal fabrics — full modesty in one piece, no styling required.",
    heroImageAlt: "Closed pull-over abayas in deep neutral colors",
    categorySlugs: ["closed-abayas", "closed-pullover-abayas", "abayas-style"],
    productKeywords: ["closed abaya", "pullover abaya", "pull-on abaya"],
    intro:
      "Closed abayas are the simplest, most coverage-forward abaya silhouette — pull on, walk out, fully modest. We stock closed abayas from everyday cotton and crepe to formal embroidered and satin pieces.",
    sections: [
      {
        heading: "When to choose a closed abaya",
        body:
          "Daily prayer and mosque visits — fully covered in one piece.\n\nWork in modesty-conscious environments.\n\nTravel — minimal styling, maximum coverage.\n\nFormal events — embroidered closed abayas are evening-ready.",
      },
      {
        heading: "Closed abaya fabrics",
        body:
          "Crepe and nidha: classic everyday weights with structured drape.\n\nCotton and linen blends: breathable for warm climates.\n\nSatin and silk: dressier finishes for events and Eid.\n\nEmbroidered and beaded: bridal and formal occasions.",
      },
      {
        heading: "Sizing & length tips for closed abayas",
        body:
          "Standard closed abayas run 54–58 inches. Tall versions go up to 60–62 inches — measure shoulder to ankle in a straight line.\n\nFor a relaxed drape, size up if you plan to layer over thick clothing in winter.\n\nLook for snap-cuff or zip-cuff sleeves if you frequently make wudu — they roll up and stay put.\n\nMost vendors stock plus sizes through US 24+ with extended length options.",
      },
    ],
    faqs: [
      { q: "What's the difference between closed and open abayas?", a: "Closed abayas pull over the head with no opening; open abayas have a front opening you wear over an outfit." },
      { q: "Can I wear a closed abaya in summer?", a: "Yes — choose lightweight crepe, cotton, or linen blends in light colors." },
      { q: "Do closed abayas come with pockets?", a: "Many do — check listings for pocket options." },
      { q: "Can I wear pants under a closed abaya?", a: "Yes — most are loose enough to wear over any modest base layer." },
      { q: "How do I wash a closed abaya?", a: "Crepe and nida abayas are machine washable cold on delicate; embellished and silk pieces should be dry cleaned." },
    ],
    related: [
      { label: "Open-front abayas", href: "/open-front-abayas" },
      { label: "Kimono abayas", href: "/kimono-abayas-shop" },
      { label: "Butterfly abayas", href: "/butterfly-abayas-shop" },
      { label: "All abayas", href: "/abayas" },
    ],
  },

  // 23. KIMONO ABAYAS
  {
    slug: "kimono-abayas-shop",
    primaryKeyword: "kimono abayas",
    title: "Kimono Abayas: Wide-Sleeve Modern Abayas | HijabiStore",
    metaDescription:
      "Shop kimono abayas — modern, flowing kimono-style abayas for a contemporary modest look. Free US shipping on $75+.",
    h1: "Kimono Abayas — Modern, Flowy, Statement Sleeves",
    heroSubtitle: "Kimono-inspired abayas with dramatic wide sleeves and modern cuts — elevate everyday modesty effortlessly.",
    heroImageAlt: "Kimono-style abayas with wide sleeves draping elegantly",
    categorySlugs: ["kimono-abayas", "cl-kimono-abayas", "abayas-style"],
    productKeywords: ["kimono abaya", "kimono"],
    intro:
      "Kimono abayas merge traditional modesty with modern silhouettes — the wide-sleeve, often-belted cut creates instant elegance. Shop kimono abayas from independent designers in everyday and formal weights.",
    sections: [
      {
        heading: "What defines a kimono abaya",
        body:
          "Wide, flowy sleeves — often bell-shaped or batwing.\n\nOpen front with a tie or belt for shape.\n\nFloor-length cut for full coverage.\n\nOften paired with embellished trims, embroidery, or contrast piping.",
      },
      {
        heading: "How to style kimono abayas",
        body:
          "Belt at the waist for a defined silhouette.\n\nLayer over a fitted maxi dress or tunic-and-pants for clean lines.\n\nChoose tonal accessories — let the kimono drape be the focal point.",
      },
      {
        heading: "When to wear a kimono abaya",
        body:
          "Casual days: cotton or linen kimono abayas over jeans and a long-sleeve tee for effortless modest styling.\n\nWork: structured crepe kimonos in neutral tones over a midi dress or tucked blouse and trousers.\n\nEvening events: satin or embroidered kimonos belted at the waist with a tonal silk hijab.\n\nTravel: lightweight kimono abayas pack flat and dress up multiple outfits across a trip.",
      },
    ],
    faqs: [
      { q: "Are kimono abayas formal?", a: "They span everyday cotton kimonos to embroidered satin formal pieces — choose by fabric." },
      { q: "How do I wash a kimono abaya?", a: "Most are machine-washable on cold delicate; embellished or silk pieces should be hand washed or dry cleaned." },
      { q: "Do kimono abayas come in plus sizes?", a: "Yes — many vendors offer extended sizes through US 24+." },
      { q: "Can I wear a kimono abaya open?", a: "Yes — many are designed to drape open and look intentional that way." },
      { q: "What's the difference between a kimono abaya and a regular open abaya?", a: "Kimono abayas have wider, often bell-shaped sleeves and a relaxed drape; standard open abayas are usually narrower-sleeved." },
    ],
    related: [
      { label: "Butterfly abayas", href: "/butterfly-abayas-shop" },
      { label: "Open-front abayas", href: "/open-front-abayas" },
      { label: "Kaftans", href: "/kaftans" },
      { label: "All abayas", href: "/abayas" },
    ],
  },

  // 24. BUTTERFLY ABAYAS
  {
    slug: "butterfly-abayas-shop",
    primaryKeyword: "butterfly abayas",
    title: "Butterfly Abayas: Dramatic Cape-Sleeve Abayas | HijabiStore",
    metaDescription:
      "Shop butterfly abayas — dramatic, wide-sleeve butterfly abaya designs for a statement modest look. Free US shipping on $75+.",
    h1: "Butterfly Abayas — Cape Sleeves, Dramatic Drape",
    heroSubtitle: "Butterfly-cut abayas with sweeping cape sleeves — for the moments when you want to make an entrance.",
    heroImageAlt: "Butterfly abayas with dramatic cape sleeves on display",
    categorySlugs: ["butterfly-abayas", "abayas-style"],
    productKeywords: ["butterfly abaya"],
    intro:
      "Butterfly abayas are the ultimate statement modest piece — wide, attached cape sleeves create movement and drama with every step. Ideal for Eid, weddings, and formal evenings.",
    sections: [
      {
        heading: "When to wear a butterfly abaya",
        body:
          "Eid celebrations — the silhouette photographs beautifully.\n\nWedding guest looks — pair with statement jewelry and silk hijab.\n\nFormal evenings and gala-style events.\n\nReligious holidays where you want elevated modesty.",
      },
      {
        heading: "Choosing your butterfly abaya",
        body:
          "Look for proper sleeve weight — too light and the cape collapses; too heavy and it feels burdensome.\n\nSatin, crepe, and silk drape best.\n\nEmbroidered hems and necklines add polish without overpowering.",
      },
      {
        heading: "Caring for butterfly abayas",
        body:
          "Hang on a wide padded hanger to preserve sleeve shape — never fold along sleeve seams.\n\nDry clean embroidered or beaded butterfly abayas to protect detailing.\n\nSteam (don't iron) to remove wrinkles without flattening the cape drape.\n\nFor travel, fold sleeves carefully and pack on top of other items in your suitcase.",
      },
    ],
    faqs: [
      { q: "Are butterfly abayas formal only?", a: "Mostly — they're designed for events. Lightweight everyday butterfly cuts exist but are rarer." },
      { q: "Are butterfly abayas comfortable?", a: "Yes — the loose silhouette is unrestrictive, though the wide sleeves take getting used to in tight spaces." },
      { q: "What underlayer works best?", a: "A fitted maxi dress or slim long-sleeve dress so the silhouette stays clean." },
      { q: "Do butterfly abayas wrinkle?", a: "Crepe and satin resist wrinkles well; chiffon butterfly abayas need more careful packing." },
      { q: "Can I wear a butterfly abaya in winter?", a: "Yes — choose heavier crepe or wool-blend butterfly abayas, and layer a fitted long-sleeve dress underneath for warmth." },
    ],
    related: [
      { label: "Kimono abayas", href: "/kimono-abayas-shop" },
      { label: "Modest eveningwear", href: "/modest-eveningwear" },
      { label: "Eid outfits", href: "/eid-outfits" },
      { label: "All abayas", href: "/abayas" },
    ],
  },

  // 25. EID OUTFITS
  {
    slug: "eid-outfits",
    primaryKeyword: "Eid outfits",
    title: "Eid Outfits: Modest Eid Dresses, Abayas & Sets | HijabiStore",
    metaDescription:
      "Shop Eid outfits & Eid dresses — modest abayas, matching sets & special occasion wear for Eid celebrations. Free US shipping on $75+.",
    h1: "Eid Outfits — Dresses, Abayas & Statement Modest Looks",
    heroSubtitle: "Curated Eid edits from independent modest fashion vendors — embroidered abayas, kaftans, and statement modest dresses for the celebration.",
    heroImageAlt: "Eid outfit collection with embellished abayas and modest dresses",
    categorySlugs: ["eid-collection", "occasion-celebrations"],
    productKeywords: ["eid", "eid abaya", "eid dress"],
    intro:
      "Eid is the moment to dress your best — and our Eid edit brings together embroidered abayas, kaftans, modest gowns, and matching family looks from vetted vendors. Shop early, ship free over $75, and arrive with intention.",
    sections: [
      {
        heading: "Eid outfit ideas",
        body:
          "Statement embroidered abaya in a jewel tone — pair with a tonal silk hijab.\n\nFloor-length kaftan with metallic detailing — for the celebratory dinner.\n\nFitted modest gown with a belted overlay — for evening Eid prayers and gatherings.\n\nMatching mom-and-daughter abayas or kaftans — make it a family moment.",
      },
      {
        heading: "When to shop for Eid",
        body:
          "Order 3–4 weeks before Eid to allow for shipping and minor alterations.\n\nVendors restock heavily 6 weeks out — early shoppers get the best selection.\n\nLast-minute Eid? Filter for in-stock items shipping from US warehouses for fastest delivery.",
      },
      {
        heading: "Eid styling for the whole family",
        body:
          "Mom & daughter: matching abayas or kaftans in coordinating colors create memorable Eid photos.\n\nMen and boys: pair women's tonal abayas with classic thobes or kurtas for a cohesive family look.\n\nLittle ones: many vendors stock children's modest sets that mirror adult styles in size 2T–14.\n\nHijab and accessories: a tonal silk hijab, statement earrings, and a clutch tie any Eid look together.",
      },
    ],
    faqs: [
      { q: "When should I order my Eid outfit?", a: "Ideally 3–4 weeks ahead. Many vendors ship from the US within 2–5 business days." },
      { q: "Do you have matching mom-and-daughter Eid sets?", a: "Yes — many vendors offer mini-me abayas and kaftans designed to match adult pieces." },
      { q: "Are returns possible on Eid orders?", a: "Yes — standard 30-day returns apply to most Eid pieces in original condition with tags." },
      { q: "What's trending for Eid this year?", a: "Pearl-embellished abayas, jewel-tone kaftans, and tonal silk hijabs are leading the season." },
      { q: "Do you offer plus-size Eid outfits?", a: "Yes — many vendors stock formal Eid pieces through US 24+ with extended length options." },
    ],
    related: [
      { label: "Embroidered abayas", href: "/embroidered-abayas-shop" },
      { label: "Kaftans", href: "/kaftans" },
      { label: "Modest eveningwear", href: "/modest-eveningwear" },
      { label: "All abayas", href: "/abayas" },
    ],
  },

  // 26. EMBROIDERED & LUXURY ABAYAS
  {
    slug: "embroidered-abayas-shop",
    primaryKeyword: "embroidered abayas",
    title: "Embroidered Abayas: Luxury Embellished Abayas | HijabiStore",
    metaDescription:
      "Shop embroidered abayas — handcrafted, beautifully embroidered abayas for special occasions. Free US shipping on $75+.",
    h1: "Embroidered Abayas — Luxury, Beaded, Pearl-Trimmed",
    heroSubtitle: "Hand-embellished abayas with pearl, beadwork, and intricate threadwork — for weddings, Eid, and formal evenings.",
    heroImageAlt: "Embroidered luxury abayas with pearl and bead detailing",
    categorySlugs: ["embroidered-abayas", "embroidered-luxury-abayas", "pearl-abayas", "lace-abayas"],
    productKeywords: ["embroidered abaya", "pearl abaya", "lace abaya", "luxury abaya"],
    intro:
      "Embroidered abayas turn a modest silhouette into a statement piece. Whether you want delicate pearl trims for everyday luxury or full bridal-style beading for a wedding, we curate embellished abayas from independent designers around the world.",
    sections: [
      {
        heading: "Embroidered abaya styles",
        body:
          "Pearl trim: discreet luxury along sleeves and neckline.\n\nFull beadwork: bridal-level embellishment for show-stopping looks.\n\nLace overlay: feminine and romantic, often paired with satin underlay.\n\nThread embroidery: tonal or contrasting motifs along borders and bodice.",
      },
      {
        heading: "Caring for embroidered abayas",
        body:
          "Always check the care label — most embellished abayas are hand wash or dry clean only.\n\nStore on a padded hanger to protect beadwork.\n\nUse a soft cloth bag for travel to prevent snagging.",
      },
      {
        heading: "When to wear an embroidered abaya",
        body:
          "Weddings & Nikkah: pearl-trim and bridal-level beaded styles in soft tones (cream, blush, dove grey).\n\nEid celebrations: jewel-tone embroidered abayas (emerald, sapphire, plum, gold).\n\nFormal evenings & galas: classic black or navy with metallic threadwork.\n\nUpscale family gatherings: subtle thread embroidery on tonal base fabric for refined elegance.",
      },
    ],
    faqs: [
      { q: "Are embroidered abayas wedding-appropriate?", a: "Absolutely — many are designed specifically for weddings, Nikkah ceremonies, and bridal parties." },
      { q: "How do I clean an embroidered abaya?", a: "Hand wash cold or dry clean. Avoid machine washing to protect beadwork." },
      { q: "Can I wear an embroidered abaya casually?", a: "Subtle pearl-trim or thread-embroidered styles work for upscale dinners and family gatherings." },
      { q: "Do you have plus-size embroidered abayas?", a: "Yes — many vendors stock embellished abayas through US 24+." },
      { q: "How long do embroidered abayas last?", a: "With careful hand washing or dry cleaning and padded-hanger storage, embroidered abayas can last 10+ years as keepsake pieces." },
    ],
    related: [
      { label: "Eid outfits", href: "/eid-outfits" },
      { label: "Modest eveningwear", href: "/modest-eveningwear" },
      { label: "Wedding-guest dresses", href: "/modest-wedding-guest-dresses" },
      { label: "All abayas", href: "/abayas" },
    ],
  },

  // 27. MODEST WEDDING GUEST DRESSES
  {
    slug: "modest-wedding-guest-dresses",
    primaryKeyword: "modest wedding guest dresses",
    title: "Modest Wedding Guest Dresses | HijabiStore",
    metaDescription:
      "Shop modest wedding guest dresses — elegant, full-coverage dresses perfect for wedding celebrations. Free US shipping on $75+.",
    h1: "Modest Wedding Guest Dresses — Elegant, Covered, Photo-Ready",
    heroSubtitle: "Floor-length, long-sleeve gowns and embellished maxis curated for hijabi wedding guests.",
    heroImageAlt: "Modest wedding guest dresses on display in jewel tones",
    categorySlugs: ["wedding-guest", "wedding-formal", "evening-formal-dresses", "evening-dresses"],
    productKeywords: ["wedding guest", "wedding dress", "formal dress", "evening gown"],
    intro:
      "Finding a modest wedding guest dress shouldn't take six tabs of compromise. We've curated long-sleeve maxi gowns, embellished modest dresses, and formal modest sets — all wedding-appropriate without sacrificing coverage.",
    sections: [
      {
        heading: "What makes a great modest wedding guest dress",
        body:
          "Floor length or maxi cut for formal occasions.\n\nLong sleeves — or worn with a coordinated long-sleeve underlayer.\n\nNo high slits, sheer panels, or low necklines.\n\nFabric weight that drapes (crepe, satin, chiffon, silk).",
      },
      {
        heading: "Color guide for wedding guests",
        body:
          "Avoid white, ivory, and champagne unless the bride explicitly invites them.\n\nJewel tones (emerald, sapphire, plum), warm neutrals, and dusty pastels are safe and photograph well.\n\nMatch your hijab in tonal silk or chiffon for a polished look.",
      },
      {
        heading: "How to style a modest wedding guest dress",
        body:
          "Belt embellished gowns at the waist to define your silhouette.\n\nChoose a tonal silk or satin hijab — let the dress detailing be the focal point.\n\nAdd statement earrings (pinned to the hijab) and a small clutch — keep accessories minimal.\n\nFinish with closed-toe heels or pointed flats in a neutral that complements your hijab.",
      },
    ],
    faqs: [
      { q: "Can I wear an abaya as a wedding guest?", a: "Absolutely — embroidered or satin abayas are wedding-appropriate and elegant." },
      { q: "What if the dress isn't long-sleeved?", a: "Layer with a fitted long-sleeve top in matching color, or choose a wrap or duster overlay." },
      { q: "Are returns possible?", a: "Yes — standard 30-day returns apply, so you can order multiple options and decide at home." },
      { q: "Do you have plus-size modest gowns?", a: "Yes — many vendors stock formal modest dresses through US 24+ with extended length options." },
      { q: "What shoes go with a modest wedding guest dress?", a: "Closed-toe heels, pointed flats, or block-heel sandals in a neutral that ties to your hijab color work best." },
    ],
    related: [
      { label: "Modest eveningwear", href: "/modest-eveningwear" },
      { label: "Embroidered abayas", href: "/embroidered-abayas-shop" },
      { label: "Kaftans", href: "/kaftans" },
      { label: "Modest dresses", href: "/modest-dresses" },
    ],
  },

  // 28. MODEST CARDIGANS & DUSTERS
  {
    slug: "modest-cardigans-dusters",
    primaryKeyword: "modest cardigans and dusters",
    title: "Modest Cardigans & Long Dusters | HijabiStore",
    metaDescription:
      "Shop modest cardigans and long duster coats — versatile layering for any modest outfit. Free US shipping on $75+.",
    h1: "Modest Cardigans & Long Dusters — Layer Everything",
    heroSubtitle: "Long cardigans, dusters, and layering pieces that turn any outfit modest in one step.",
    heroImageAlt: "Long modest cardigans and dusters in neutral tones",
    categorySlugs: ["long-cardigans-dusters", "outerwear", "layering-pieces"],
    productKeywords: ["cardigan", "duster", "layering"],
    intro:
      "A great long cardigan or duster makes any non-modest outfit work. Throw one over a short-sleeve dress, a fitted top, or even loungewear — instant coverage with intentional styling.",
    sections: [
      {
        heading: "Why long cardigans are wardrobe essentials",
        body:
          "They convert a non-modest top into a modest outfit instantly.\n\nThey layer over dresses for transitional weather.\n\nThey work for travel — one duster covers multiple outfit looks.",
      },
      {
        heading: "Cardigan vs duster: what's the difference?",
        body:
          "Cardigans are typically knit, ranging from cropped to tunic-length.\n\nDusters are longer (often ankle-length), can be knit or woven, and often more structured.",
      },
      {
        heading: "How to style cardigans and dusters",
        body:
          "Throw a long duster over a fitted long-sleeve tee and wide-leg trousers for an effortless modest workday look.\n\nLayer a chunky knit cardigan over a maxi dress for transitional weather styling.\n\nBelt a longline cardigan at the waist to define your silhouette.\n\nFor travel, pack one neutral duster — it dresses up casual outfits and adds warmth on planes.",
      },
    ],
    faqs: [
      { q: "Can I wear a cardigan over an abaya?", a: "Open cardigans can layer over open abayas in colder weather for added warmth and texture." },
      { q: "What's the most versatile color?", a: "Camel, black, ivory, and olive layer over almost any outfit." },
      { q: "Are dusters formal enough for events?", a: "Embroidered or satin dusters can absolutely be evening-appropriate." },
      { q: "Do you have plus-size dusters?", a: "Yes — many vendors stock through US 24+ with extended length options for tall hijabis." },
      { q: "How do I wash long cardigans and dusters?", a: "Knit pieces should be hand washed or machine washed cold on delicate, then laid flat to dry to retain shape. Dry clean structured woven dusters." },
    ],
    related: [
      { label: "Open-front abayas", href: "/open-front-abayas" },
      { label: "Modest tops & tunics", href: "/modest-tops-tunics" },
      { label: "Modest workwear", href: "/modest-workwear" },
      { label: "All clothing", href: "/shop" },
    ],
  },
  // ── Phase 3: Color, fabric & occasion landing pages ──

  // BLACK HIJABS
  {
    slug: "black-hijabs",
    primaryKeyword: "black hijabs",
    title: "Black Hijabs — Classic Black Hijabs in Every Fabric | HijabiStore",
    metaDescription:
      "Shop black hijabs in jersey, chiffon, silk, modal & more. The wardrobe essential every hijabi needs. Free US shipping on $75+.",
    h1: "Black Hijabs — Timeless, Versatile, Always Essential",
    heroSubtitle:
      "The one hijab every wardrobe needs — black hijabs in jersey, chiffon, silk, modal and crinkle, from independent modest fashion brands.",
    heroImageAlt: "Folded black hijabs in chiffon, jersey and silk on a neutral background",
    categorySlugs: ["hijabs", "hijab"],
    productKeywords: ["black hijab", "black scarf", "black shawl"],
    intro:
      "If you only own one hijab, make it black. A well-made black hijab pairs with everything in your wardrobe — workwear, abayas, evening looks, denim — and quietly elevates the whole outfit. We carry black hijabs in every fabric a hijabi could want: matte chiffon for events, soft jersey for daily wear, modal and bamboo blends for hot weather, and silk for special occasions.",
    sections: [
      {
        heading: "Why a black hijab is the ultimate essential",
        body:
          "Black is the most versatile color in modest fashion — it reads professional at work, elegant at events, and effortless on busy days.\n\nIt hides minor stains and pilling far better than light colors, so a black hijab lasts visibly longer.\n\nIt photographs beautifully and works in every season — no other shade is this forgiving.",
      },
      {
        heading: "Choosing the right fabric for your black hijab",
        body:
          "Black jersey: non-slip, beginner-friendly, soft enough for daily wear. The everyday workhorse.\n\nBlack chiffon: airy, semi-sheer, ideal for layering with an undercap for events and Eid.\n\nBlack modal & bamboo: breathable and skin-friendly — best for hot weather and sensitive skin.\n\nBlack silk or satin: high-shine luxury for evening wear and weddings.\n\nBlack crinkle cotton: matte texture and easy to style without ironing.",
      },
      {
        heading: "Styling tips for black hijabs",
        body:
          "Pair a matte black hijab with neutral abayas (camel, taupe, ivory) for an editorial monochrome look.\n\nWear a black silk hijab with jewel-toned dresses — emerald, sapphire, ruby — for evening contrast.\n\nFor work, match a black jersey hijab to your blazer for a polished, intentional look.\n\nKeep two black hijabs in rotation: one matte for daytime, one with sheen for evening.",
      },
      {
        heading: "Caring for black hijabs",
        body:
          "Wash dark hijabs separately or with other darks to prevent fading.\n\nTurn inside out, use cool water, and skip bleach or optical brighteners.\n\nLay flat or hang to dry — direct sun can fade rich black to a dusty grey over time.",
      },
    ],
    faqs: [
      { q: "What's the best black hijab for everyday?", a: "A black jersey or modal hijab — both grip naturally, breathe well, and require zero pinning." },
      { q: "Do black hijabs fade?", a: "Yes, all black fabrics fade over time. Wash cold, inside out, and dry away from direct sun to extend the life of the rich color." },
      { q: "Is a black chiffon hijab see-through?", a: "Most quality chiffons are semi-sheer. Wear a black underscarf cap underneath for full opacity." },
      { q: "Can I wear a black hijab to a wedding?", a: "Absolutely — black silk, satin, or chiffon hijabs are wedding-appropriate, especially paired with jewel-tone or metallic dresses." },
      { q: "What length black hijab should I buy?", a: "A 27\" x 70\" rectangle is the most versatile. For fuller chest coverage, look for maxi lengths up to 80\"." },
      { q: "Do you offer free shipping on black hijabs?", a: "Yes — free standard US shipping on all orders over $75, and we ship to 100+ countries." },
    ],
    related: [
      { label: "White hijabs", href: "/white-hijabs" },
      { label: "Nude & neutral hijabs", href: "/nude-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
      { label: "Underscarves", href: "/underscarves" },
    ],
  },

  // WHITE HIJABS
  {
    slug: "white-hijabs",
    primaryKeyword: "white hijabs",
    title: "White Hijabs — Crisp White Hijabs for Every Occasion | HijabiStore",
    metaDescription:
      "Shop white hijabs in jersey, chiffon, silk & modal. Perfect for Eid, nikah, and everyday elegance. Free US shipping on $75+.",
    h1: "White Hijabs — Clean, Bright, Effortlessly Elegant",
    heroSubtitle:
      "Crisp white and ivory hijabs in jersey, chiffon, silk and modal — perfect for Eid, nikah, Umrah, and brightening up everyday looks.",
    heroImageAlt: "Stack of white and ivory hijabs in chiffon and silk",
    categorySlugs: ["hijabs", "hijab"],
    productKeywords: ["white hijab", "ivory hijab", "off-white hijab"],
    intro:
      "A great white hijab does what no other color can: it makes your skin glow, brightens neutral outfits, and feels instantly fresh. We carry true whites, soft ivories, and warm off-whites in every fabric — so whether you're getting ready for Eid, your nikah, Umrah, or just want a clean go-to for spring, you'll find your match.",
    sections: [
      {
        heading: "When to wear a white hijab",
        body:
          "Eid mornings, nikah ceremonies, baby showers, and bridal showers all call for white or ivory.\n\nUmrah and Hajj — white hijabs in lightweight, breathable fabric are ideal for the heat and modesty requirements.\n\nWith pastel and floral outfits in spring, white softens and unifies.\n\nWith all-black ensembles for a fresh, editorial monochrome contrast.",
      },
      {
        heading: "Best fabrics for white hijabs",
        body:
          "White chiffon: classic for events and weddings — pair with an underscarf cap for opacity.\n\nWhite jersey or modal: everyday-friendly, soft, breathable, and easy to wash.\n\nWhite silk or satin: bridal-ready, luminous, and photographs beautifully.\n\nIvory and off-white blends are more flattering on warm skin tones than pure stark white.",
      },
      {
        heading: "Keeping white hijabs white",
        body:
          "Wash whites separately and avoid mixing with bright colors that bleed.\n\nUse a small amount of oxygen-based brightener every few washes to combat dinginess.\n\nNever leave makeup or foundation on a white hijab — pre-treat stains immediately with cold water.\n\nLine dry in indirect sunlight for natural brightening without fabric damage.",
      },
      {
        heading: "Styling white hijabs",
        body:
          "Pair an ivory chiffon hijab with a blush or champagne abaya for soft Eid elegance.\n\nWear a white silk hijab with a navy or emerald gown for a high-contrast event look.\n\nKeep an everyday white modal hijab for spring and summer — it pairs with denim, linen, and pastels effortlessly.",
      },
    ],
    faqs: [
      { q: "Is white or ivory more flattering?", a: "Ivory and off-white tend to flatter warm skin tones, while pure white is striking on cool skin tones. Try both and see which makes your skin glow." },
      { q: "Can I wear a white hijab daily?", a: "Yes — choose machine-washable jersey or modal for daily wear and wash promptly to keep it bright." },
      { q: "Are white hijabs see-through?", a: "Lightweight whites can be slightly sheer. Wear a matching underscarf cap underneath for full coverage." },
      { q: "What's the best white hijab for Umrah?", a: "A breathable modal, bamboo, or lightweight cotton white hijab — all comfortable in heat and easy to wash." },
      { q: "Do you have white silk hijabs for weddings?", a: "Yes — we carry white silk and satin hijabs ideal for bridal looks and nikah ceremonies." },
      { q: "Can I bleach a white hijab?", a: "Avoid chlorine bleach — it weakens fibers. Use oxygen-based brighteners instead." },
    ],
    related: [
      { label: "Black hijabs", href: "/black-hijabs" },
      { label: "Nude & neutral hijabs", href: "/nude-hijabs" },
      { label: "Umrah clothing", href: "/umrah-clothing" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // NUDE HIJABS
  {
    slug: "nude-hijabs",
    primaryKeyword: "nude hijabs",
    title: "Nude & Neutral Hijabs — Skin-Tone Matching Hijabs | HijabiStore",
    metaDescription:
      "Shop nude, beige, tan & neutral hijabs in every fabric. Find your perfect skin-tone match. Free US shipping on $75+.",
    h1: "Nude & Neutral Hijabs — Your Perfect Skin-Tone Match",
    heroSubtitle:
      "Nude, beige, tan, camel and warm neutral hijabs in chiffon, jersey, silk and modal — skin-tone matching shades for every undertone.",
    heroImageAlt: "Range of nude, beige and tan hijabs arranged on a neutral surface",
    categorySlugs: ["hijabs", "hijab"],
    productKeywords: ["nude", "beige", "tan", "neutral", "camel", "taupe", "sand"],
    intro:
      "The right nude hijab is a quiet superpower — it lengthens your face, blends seamlessly into modest layered looks, and works with literally everything. We carry nude hijabs in a full range of undertones (cool, warm, golden, deep) so every skin tone has a true match. From soft beige chiffons for events to camel jersey for daily wear, the neutrals collection is the most-requested edit on the site.",
    sections: [
      {
        heading: "Finding your perfect nude shade",
        body:
          "Cool undertones: try blush-nude, soft pink, and ash beige.\n\nWarm undertones: try camel, golden tan, and honey beige.\n\nDeep skin tones: try chocolate, mocha, espresso, and rich caramel — these are the true \"nudes\" for deeper complexions.\n\nMid skin tones: warm taupe, latte, and sand all flatter beautifully.",
      },
      {
        heading: "Why every wardrobe needs a nude hijab",
        body:
          "Nudes pair with every other color — black, navy, pastels, jewel tones, prints — without competing.\n\nThey photograph beautifully and read as polished and intentional.\n\nThey lengthen the face and frame the features without drawing focus away from your outfit.\n\nThey work for work, weddings, daily life, and travel — a single nude hijab can carry a whole capsule wardrobe.",
      },
      {
        heading: "Best fabrics for nude hijabs",
        body:
          "Nude chiffon: luxurious and slightly sheer — perfect for events and Eid.\n\nNude jersey or modal: soft, grippy, daily-friendly.\n\nNude silk or satin: brings shine and dimension to monochrome looks.",
      },
      {
        heading: "Styling neutral hijabs",
        body:
          "Pair a camel hijab with a black abaya and gold accessories for an effortless luxe look.\n\nLayer ivory and beige tones together for a tonal monochrome moment.\n\nUse a deep chocolate or espresso hijab to add warmth to autumn and winter outfits.",
      },
    ],
    faqs: [
      { q: "What is a 'nude' hijab?", a: "Nude hijabs come in skin-tone-adjacent shades — beige, tan, camel, taupe, mocha, espresso — designed to flatter every complexion." },
      { q: "How do I find my skin-tone match?", a: "Hold the hijab next to your jawline in natural light. The right shade will make your skin look brighter; the wrong one will make it look dull." },
      { q: "Are nude hijabs see-through?", a: "Lightweight chiffons can be slightly sheer. Pair with a matching nude underscarf for full opacity." },
      { q: "Do nude hijabs come in deep skin tones?", a: "Yes — we carry chocolate, mocha, and espresso hijabs that act as true nudes for deeper complexions." },
      { q: "What's the difference between beige and camel?", a: "Beige is lighter and cooler-leaning; camel is warmer with a golden-brown undertone." },
      { q: "Can I wear nude to a wedding?", a: "Absolutely — soft champagne, blush nude, and warm beige are wedding-appropriate and beautiful in photos." },
    ],
    related: [
      { label: "Black hijabs", href: "/black-hijabs" },
      { label: "White hijabs", href: "/white-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
      { label: "Modal hijabs", href: "/modal-hijabs" },
    ],
  },

  // MODAL HIJABS
  {
    slug: "modal-hijabs",
    primaryKeyword: "modal hijabs",
    title: "Modal Hijabs — Soft, Breathable Modal Fabric Hijabs | HijabiStore",
    metaDescription:
      "Shop modal hijabs — silky-soft, breathable, and lightweight. The premium everyday hijab fabric. Free US shipping on $75+.",
    h1: "Modal Hijabs — Ultra-Soft, Breathable, All-Day Comfort",
    heroSubtitle:
      "Modal hijabs combine the silky softness of natural fibers with the breathability of cotton — the everyday upgrade your wardrobe deserves.",
    heroImageAlt: "Soft modal hijabs draped on a neutral surface",
    categorySlugs: ["hijabs", "hijab"],
    productKeywords: ["modal", "modal hijab", "modal scarf"],
    intro:
      "Modal is the quietly luxurious fiber every modest fashion brand has been moving toward — it feels like silk against the skin but breathes like cotton, drapes beautifully, and resists pilling. Our modal hijabs are the most-recommended everyday option from our editors: soft enough for sensitive skin, light enough for hot climates, and structured enough to hold a clean drape.",
    sections: [
      {
        heading: "What makes modal fabric special",
        body:
          "Modal is a semi-synthetic fiber made from beech tree pulp — a natural source processed for softness and durability.\n\nIt's significantly softer than cotton, more breathable than polyester, and resists shrinking and pilling.\n\nIt holds dye beautifully, so colors look richer and stay vibrant longer than on most fibers.\n\nIt naturally wicks moisture, making it ideal for hot weather and sensitive skin.",
      },
      {
        heading: "Why modal hijabs win at everyday wear",
        body:
          "They drape cleanly without slipping — the slight weight and natural grip mean less re-adjusting throughout the day.\n\nThey breathe in summer and layer well in winter — true year-round wear.\n\nThey're machine washable on cold and tumble-dry friendly on low heat.\n\nThey're gentle on sensitive skin, eczema, and acne-prone foreheads.",
      },
      {
        heading: "Modal vs jersey vs cotton",
        body:
          "Modal: silkiest, most breathable, slightly more premium price.\n\nJersey: stretchier, grips harder, more affordable.\n\nCotton: matte texture, classic feel, can wrinkle without ironing.\n\nFor most people, a 50/50 wardrobe of modal and jersey hijabs covers nearly every situation.",
      },
      {
        heading: "Caring for your modal hijabs",
        body:
          "Machine wash cold on delicate, with similar colors.\n\nTumble dry low or hang dry — modal handles dryers better than jersey.\n\nIron on low heat if needed, ideally while slightly damp.\n\nAvoid fabric softeners — they coat the fibers and reduce breathability over time.",
      },
    ],
    faqs: [
      { q: "What is modal fabric?", a: "Modal is a semi-synthetic fiber made from beech tree pulp. It's softer than cotton, more breathable than polyester, and naturally moisture-wicking." },
      { q: "Are modal hijabs slippery?", a: "Modal has slight natural grip — less than jersey but more than chiffon. Most people don't need an underscarf cap with modal." },
      { q: "Can modal hijabs go in the dryer?", a: "Yes, on low heat. Modal is more dryer-friendly than jersey or chiffon." },
      { q: "Are modal hijabs good for hot weather?", a: "Excellent — modal is breathable, lightweight, and naturally wicks moisture." },
      { q: "Do modal hijabs wrinkle?", a: "Minimally — modal has natural drape and rarely needs ironing after a wash." },
      { q: "Are modal hijabs eco-friendly?", a: "Modal is more sustainable than synthetic fibers since it comes from a renewable wood source, though processing varies by manufacturer." },
    ],
    related: [
      { label: "Jersey hijabs", href: "/jersey-hijabs" },
      { label: "Chiffon hijabs", href: "/chiffon-hijabs" },
      { label: "Silk hijabs", href: "/silk-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // UNDERSCARVES
  {
    slug: "underscarves",
    primaryKeyword: "underscarves",
    title: "Underscarves & Undercaps — Hijab Base Layers | HijabiStore",
    metaDescription:
      "Shop underscarves, undercaps, tube caps & tie-back caps. Essential hijab base layers for comfort and grip. Free US shipping on $75+.",
    h1: "Underscarves & Undercaps — The Foundation of Every Hijab Look",
    heroSubtitle:
      "Tube caps, tie-back caps, ninja undercaps, and full underscarves — the unsung essentials that keep your hijab in place all day.",
    heroImageAlt: "Selection of underscarves and undercaps in neutral colors",
    categorySlugs: ["underscarves", "hijab-caps", "underscarves-hijab-caps", "accessories"],
    productKeywords: ["underscarf", "undercap", "tube cap", "tie-back", "ninja cap", "bonnet"],
    intro:
      "An underscarf is the quiet workhorse of every hijabi's wardrobe — it stops slipping, smooths your hairline, prevents headaches from pins, and creates the clean foundation that makes your outer hijab look polished. We carry tube caps, tie-back caps, ninja undercaps, and full coverage underscarves in soft cotton, jersey, and bamboo — in every shade you'd ever need to match.",
    sections: [
      {
        heading: "Underscarf styles, explained",
        body:
          "Tube cap: a simple stretchy band that covers your hairline and ears. Quick, light, and ideal under jersey or modal hijabs.\n\nTie-back cap: covers from forehead to nape with adjustable ties at the back — best for fine or slippery hair.\n\nNinja undercap: a balaclava-style cap that covers the entire head and neck — perfect under chiffon or for one-step coverage.\n\nFull underscarf: a longer piece that covers the chest area too — great for sheer outer hijabs.",
      },
      {
        heading: "Why you need an underscarf",
        body:
          "It stops your outer hijab from slipping by gripping your hair.\n\nIt protects delicate hijabs from oils and product residue from your hair.\n\nIt prevents the dreaded \"hijab headache\" caused by tight pins and constant readjusting.\n\nIt adds opacity under sheer chiffons, silks, and lightweight whites.",
      },
      {
        heading: "Best fabrics for underscarves",
        body:
          "Cotton: breathable, soft, and ideal for sensitive scalps.\n\nBamboo: naturally antimicrobial and silky against skin.\n\nJersey: stretchy and grippy, great for active days.\n\nModal: ultra-soft and lightweight for hot weather.",
      },
      {
        heading: "Choosing your shade",
        body:
          "Match the undercap to your skin tone (not your hijab) for the cleanest look — peeks of an undercap should blend with your skin, not your scarf.\n\nIf you wear black hijabs daily, keep both a skin-tone and a black undercap on hand.\n\nFor white and ivory hijabs, a white or skin-tone undercap is essential to avoid show-through.",
      },
    ],
    faqs: [
      { q: "Do I need an underscarf?", a: "Most hijabis find them essential — they prevent slipping, reduce headaches, and improve the drape of every outer hijab." },
      { q: "What's the difference between a tube cap and a ninja cap?", a: "A tube cap covers just the hairline and ears; a ninja cap covers the whole head and neck like a balaclava, giving full coverage on its own." },
      { q: "Can I sleep in an underscarf?", a: "Many hijabis sleep in soft bamboo or modal undercaps to protect their hair — bonnets are also popular for nighttime." },
      { q: "How many underscarves do I need?", a: "Most people keep 5–7: a few skin-tone, a few black, and 1–2 white. Wash and rotate frequently." },
      { q: "Are underscarves machine washable?", a: "Yes — most cotton, bamboo, and jersey underscarves are machine washable cold on delicate." },
      { q: "What's the best underscarf for thick hair?", a: "A tie-back cap with adjustable ties keeps thick hair contained without slipping." },
    ],
    related: [
      { label: "All hijabs", href: "/hijabs" },
      { label: "Jersey hijabs", href: "/jersey-hijabs" },
      { label: "Chiffon hijabs", href: "/chiffon-hijabs" },
      { label: "Hijab pins & magnets", href: "/hijab-pins-magnets-shop" },
    ],
  },

  // UMRAH CLOTHING
  {
    slug: "umrah-clothing",
    primaryKeyword: "umrah clothing",
    title: "Umrah Clothing for Women — Modest Umrah Outfits | HijabiStore",
    metaDescription:
      "Shop women's Umrah clothing — lightweight abayas, prayer dresses, comfortable hijabs & travel-friendly modest wear for your spiritual journey.",
    h1: "Umrah Clothing — Comfortable, Modest Outfits for Your Pilgrimage",
    heroSubtitle:
      "Lightweight abayas, breathable hijabs, prayer dresses and travel-ready modest essentials packed for Makkah and Madinah's heat.",
    heroImageAlt: "White and beige Umrah outfit with lightweight abaya, hijab and undercap",
    categorySlugs: ["abayas", "prayer-wear", "prayerwear", "hijabs"],
    productKeywords: ["umrah", "prayer dress", "white abaya", "ihram", "travel abaya"],
    intro:
      "Umrah clothing has one job: to keep you comfortable, modest, and focused while you move between tawaf, sa'i, and the haramain. The right pieces are loose enough to layer, light enough for the heat, and durable enough to wash daily in your hotel. We've curated lightweight abayas, breathable hijabs, easy-to-wear prayer dresses, and travel-ready modest essentials — all from independent vendors who understand exactly what's needed.",
    sections: [
      {
        heading: "What to wear for Umrah",
        body:
          "Women aren't required to wear a specific ihram garment — just modest clothing that covers everything except the hands and face.\n\nThe most practical setup is two or three loose, ankle-length abayas in light colors (white, ivory, beige, soft grey), plus 4–6 hijabs and underscarves you can rotate.\n\nAvoid tight, restrictive, or sheer pieces. You'll be on your feet for hours — comfort is non-negotiable.\n\nPack a one-piece prayer dress or two for use in the hotel and women-only sections.",
      },
      {
        heading: "Best fabrics for Umrah",
        body:
          "Cotton, modal, bamboo, and lightweight crepe are ideal — they breathe in the heat and dry quickly when handwashed.\n\nAvoid heavy nida, polyester satins, and anything embellished — they trap heat and aren't suitable for the haram.\n\nWhite and light colors stay cooler in direct sun and feel more in keeping with the spirit of pilgrimage.",
      },
      {
        heading: "Packing tips",
        body:
          "Pack 2–3 abayas, 5–6 hijabs, 4 underscarves, 2 prayer dresses, and a comfortable pair of walking sandals (kept in a labeled drawstring bag for the haram).\n\nBring a reusable water bottle, a small spray bottle for cooling, and a foldable prayer mat.\n\nLeave heavy jewelry at home — keep it simple and humble.",
      },
      {
        heading: "Wudu and ease of movement",
        body:
          "Choose abayas with sleeves that roll up easily for wudu — wide cuffs or three-quarter sleeves are practical.\n\nA hijab in a non-slip jersey or modal saves constant re-adjustment between prostrations.\n\nTie-back undercaps help your hijab stay in place during movement and sujood.",
      },
    ],
    faqs: [
      { q: "Do women wear ihram for Umrah?", a: "Women aren't required to wear a specific ihram garment — any modest clothing that covers the body except the hands and face is acceptable. Light, loose, simple clothing is preferred." },
      { q: "What color should I wear for Umrah?", a: "There's no required color, but white, ivory, beige, and soft neutrals are most common — they stay cooler in the heat and feel in keeping with the spirit of the pilgrimage." },
      { q: "Can I wear black abayas during Umrah?", a: "Yes, black is permitted, but light colors are recommended for the heat of Makkah and Madinah." },
      { q: "How many abayas should I pack?", a: "Two to three loose, lightweight abayas are usually enough — you can wash and rotate them at your hotel." },
      { q: "Do I need socks for Umrah?", a: "Many women wear thin socks for comfort and modesty during walking, though they aren't required." },
      { q: "What shoes are best for Umrah?", a: "Comfortable walking sandals or slip-on shoes — you'll remove them often, and you'll be on your feet for hours." },
    ],
    related: [
      { label: "Hajj clothing", href: "/hajj-clothing" },
      { label: "Prayer dresses", href: "/prayer-dresses" },
      { label: "Abayas", href: "/abayas" },
      { label: "White hijabs", href: "/white-hijabs" },
    ],
  },

  // HAJJ CLOTHING
  {
    slug: "hajj-clothing",
    primaryKeyword: "hajj clothing",
    title: "Hajj Clothing for Women — Modest Hajj Outfits & Essentials | HijabiStore",
    metaDescription:
      "Shop women's Hajj clothing — breathable ihram-friendly abayas, comfortable prayer dresses, lightweight hijabs & pilgrimage essentials.",
    h1: "Hajj Clothing — Practical, Modest Essentials for the Journey of a Lifetime",
    heroSubtitle:
      "Breathable abayas, lightweight hijabs, prayer dresses and pilgrimage essentials built for the demanding days of Hajj.",
    heroImageAlt: "Lightweight white Hajj abaya and hijab with travel essentials",
    categorySlugs: ["abayas", "prayer-wear", "prayerwear", "hijabs"],
    productKeywords: ["hajj", "ihram", "prayer dress", "white abaya", "pilgrimage"],
    intro:
      "Hajj is one of the most physically demanding journeys a Muslim will ever take — long days of walking, standing, and worshipping in the Saudi heat. What you wear matters. We've curated breathable abayas, comfortable prayer dresses, lightweight hijabs, and pilgrimage essentials chosen with the realities of Mina, Arafat, and Muzdalifah in mind.",
    sections: [
      {
        heading: "Hajj clothing requirements for women",
        body:
          "There is no specific ihram garment required for women — the requirement is loose, modest clothing that covers everything except the hands and face.\n\nClothing should not be tight, transparent, or revealing of the body's shape. Any color is permitted, though white and light colors are most common in keeping with the spirit of pilgrimage.\n\nFace coverings (niqab) should not be worn in ihram, though hijab is required.\n\nThe arms, legs, and feet should be covered modestly throughout.",
      },
      {
        heading: "What to pack for Hajj",
        body:
          "Three to four loose abayas in light, breathable fabric (cotton, modal, lightweight crepe).\n\nSix to eight hijabs in non-slip jersey, modal, or cotton — you'll wash daily.\n\nFour to six underscarves and tie-back caps for grip and comfort.\n\nTwo to three one-piece prayer dresses for the tents in Mina and your hotel.\n\nComfortable, well-broken-in walking sandals plus thin socks.\n\nA cross-body bag for your essentials and a labeled drawstring bag for shoes.",
      },
      {
        heading: "Best fabrics for the Saudi heat",
        body:
          "Cotton and modal are the gold standard — breathable, soft, machine washable, and quick to dry on a hotel balcony.\n\nLightweight crepe and bamboo blends drape well and stay cool.\n\nAvoid polyester, satins, heavy nida, and any embellishment — they trap heat and aren't suitable for the demanding pace of Hajj.",
      },
      {
        heading: "Practical Hajj tips",
        body:
          "Choose abayas with sleeves that roll up for wudu and pockets for small essentials.\n\nLayer with a long-sleeve cotton tee underneath in case your abaya feels thin in bright light.\n\nLabel everything — bags, shoes, hijabs — with your name in case items are misplaced.\n\nKeep two abayas in active rotation and one as a clean backup, sealed in a bag, for the days of Mina.",
      },
    ],
    faqs: [
      { q: "What does a woman wear for Hajj?", a: "Loose, modest clothing covering everything except the hands and face. There's no specific ihram garment for women — any modest, breathable abaya or set of separates works." },
      { q: "Can women wear black abayas for Hajj?", a: "Yes, black is permitted, but light colors are widely recommended because of the intense Saudi heat." },
      { q: "How many abayas should I pack for Hajj?", a: "Three to four loose, lightweight abayas — enough to rotate while washing and drying others." },
      { q: "Can I wear niqab during Hajj?", a: "No — face coverings should not be worn in ihram, though hijab is required. You can wear niqab outside the state of ihram." },
      { q: "What's the best shoe for Hajj?", a: "Comfortable, broken-in walking sandals or slip-on shoes that you can put on and take off easily for prayer." },
      { q: "What fabric is most comfortable for Hajj?", a: "Cotton, modal, and lightweight breathable crepe — they wick sweat, dry quickly, and stay cool in heat." },
    ],
    related: [
      { label: "Umrah clothing", href: "/umrah-clothing" },
      { label: "Prayer dresses", href: "/prayer-dresses" },
      { label: "Abayas", href: "/abayas" },
      { label: "White hijabs", href: "/white-hijabs" },
    ],
  },

  // MATCHING HIJAB SETS
  {
    slug: "matching-hijab-sets",
    primaryKeyword: "matching hijab sets",
    title: "Matching Hijab Sets — Hijab & Undercap Combos | HijabiStore",
    metaDescription:
      "Shop matching hijab and undercap sets — perfectly coordinated colors and fabrics for a polished look. Free US shipping on $75+.",
    h1: "Matching Hijab Sets — Coordinated Hijab & Undercap Pairings",
    heroSubtitle:
      "Perfectly coordinated hijab and undercap sets — color-matched, fabric-matched, and ready to wear with zero guesswork.",
    heroImageAlt: "Matching hijab and undercap sets in coordinated colors",
    categorySlugs: ["hijabs", "accessories"],
    productKeywords: ["matching set", "hijab set", "coordinated", "hijab and undercap"],
    intro:
      "There's something quietly luxurious about a hijab set that's already coordinated — no second-guessing whether the undercap matches, no peek of off-tone fabric. Our matching hijab sets bundle a hijab with its perfect partner undercap (or sometimes a second hijab in a complementary tone), designed by independent vendors who care about the details.",
    sections: [
      {
        heading: "Why a matching set saves you time",
        body:
          "No more digging for an undercap that almost matches — the set is already perfect.\n\nGreat for gifts, especially for new hijabis who don't yet know which undercap shapes work for them.\n\nIdeal for travel — pack one set per outfit and skip the guesswork.\n\nMatching sets photograph beautifully — every layer is intentional.",
      },
      {
        heading: "What's typically in a hijab set",
        body:
          "Most sets include one hijab plus one matching undercap (tube, tie-back, or ninja style).\n\nSome bundles include two complementary hijabs designed to layer or rotate together.\n\nGift sets often add accessories like a magnetic pin or a satin storage pouch.",
      },
      {
        heading: "Choosing your set",
        body:
          "Pick a set in your most-worn outer hijab fabric (jersey or modal for daily, chiffon for events).\n\nBuild a small capsule of three sets in black, ivory, and your skin-tone shade — that combination handles 90% of occasions.\n\nFor gifting, choose a versatile neutral or a soft pastel that flatters most skin tones.",
      },
      {
        heading: "Caring for your set",
        body:
          "Wash hijabs and undercaps together to keep colors aligned over time.\n\nFollow the most delicate fabric's care instructions — usually cold wash, lay flat to dry.\n\nStore sets together in a small drawer compartment or pouch so they stay paired.",
      },
    ],
    faqs: [
      { q: "What comes in a matching hijab set?", a: "Most sets include a hijab and a coordinating undercap. Some include two hijabs or extras like pins and a storage pouch." },
      { q: "Are matching sets a good gift?", a: "Excellent — they're polished, practical, and remove the guesswork for someone new to hijab or building a wardrobe." },
      { q: "Can I wash hijab and undercap together?", a: "Yes, washing them together helps the colors stay aligned over time." },
      { q: "Are matching sets more expensive than buying separately?", a: "Often the bundle price is slightly less than buying the same pieces individually." },
      { q: "What undercap style is best for a daily set?", a: "A tie-back undercap pairs well with most daily hijabs and offers the most secure fit." },
      { q: "Can I mix and match across sets?", a: "Absolutely — once you have a few sets, the undercaps and hijabs can be combined freely." },
    ],
    related: [
      { label: "Underscarves", href: "/underscarves" },
      { label: "All hijabs", href: "/hijabs" },
      { label: "Jersey hijabs", href: "/jersey-hijabs" },
      { label: "Chiffon hijabs", href: "/chiffon-hijabs" },
    ],
  },

  // BURKINI
  {
    slug: "burkini",
    primaryKeyword: "burkini",
    title: "Burkini & Modest Swimsuits for Muslim Women | HijabiStore",
    metaDescription:
      "Shop burkinis and modest swimsuits — full-coverage swimwear for the pool, beach & water sports. Lightweight, quick-dry fabrics. Free US shipping on $75+.",
    h1: "Burkini & Modest Swimwear — Full Coverage, Maximum Confidence",
    heroSubtitle:
      "Two-piece burkinis, full-piece swim suits, swim hijabs and rash guards — chlorine-resistant, UPF-rated, built for real swimming.",
    heroImageAlt: "Two-piece burkini with leggings, tunic and matching swim hijab",
    categorySlugs: ["modest-swimwear", "swimwear", "burkini"],
    productKeywords: ["burkini", "swim hijab", "modest swim", "rash guard", "swim leggings"],
    intro:
      "A burkini means freedom — the freedom to actually swim, to play with your kids in the pool, to surf, to do laps, to spend a whole day at the beach without a single wardrobe worry. Modern burkinis are nothing like the heavy fabric of a decade ago: they're lightweight, quick-drying, chlorine-resistant, and UPF-rated, with cuts that move with you instead of fighting you.",
    sections: [
      {
        heading: "Burkini styles to choose from",
        body:
          "Two-piece burkini: swim leggings + a long-sleeve tunic top + a separate swim hijab. The most popular setup — easy to mix and match, easy to layer.\n\nFull-piece burkini: a connected one-piece with attached head covering and built-in tunic — fewer pieces to manage.\n\nSeparates (leggings + rash guard + swim skirt): build-your-own modest swim setup, ideal for those who want maximum customization.\n\nSwim hijabs: technical caps designed to grip and stay put in water without bobbing or shifting.",
      },
      {
        heading: "Where you can wear a burkini",
        body:
          "Public and private pools throughout the US (burkinis meet hygiene standards similar to rash guards and leggings).\n\nBeaches, lakes, rivers, and any open water.\n\nWater parks, surf, paddleboarding, and water sports.\n\nWomen-only and family-friendly spas.",
      },
      {
        heading: "Best fabrics for burkinis",
        body:
          "Polyester-spandex blends with chlorine resistance hold up best in chlorinated pools.\n\nNylon-lycra is softer and quicker drying — great for ocean and beach use.\n\nLook for UPF 50+ ratings if you'll be in direct sun for hours.",
      },
      {
        heading: "Caring for your burkini",
        body:
          "Rinse in cool fresh water immediately after each swim to remove chlorine and salt.\n\nHand wash with mild detergent and lay flat to dry — never wring or tumble dry.\n\nStore folded flat away from direct sunlight to preserve color and elasticity.",
      },
    ],
    faqs: [
      { q: "What is a burkini?", a: "A burkini is a modest swimsuit covering the body, arms, legs, and hair — typically a two-piece (leggings + tunic + swim hijab) or a connected full-piece." },
      { q: "Are burkinis allowed in US public pools?", a: "Yes, burkinis are typically welcome at public and private pools because they meet hygiene standards similar to rash guards and leggings." },
      { q: "Do burkinis weigh you down in water?", a: "Modern burkinis use lightweight technical fabric that stays close to the body when wet — they don't drag or pull." },
      { q: "Can I do laps in a burkini?", a: "Absolutely — many burkinis are designed for active swimming with athletic cuts and quick-dry fabric." },
      { q: "What's the best fabric for a burkini?", a: "Polyester-spandex with chlorine resistance and UPF 50+ protection is the most versatile choice." },
      { q: "Do swim hijabs really stay on?", a: "Yes — purpose-built swim hijabs use technical fabric and snug fit to stay in place through swimming and water sports." },
    ],
    related: [
      { label: "Modest swimwear", href: "/modest-swimwear" },
      { label: "Modest activewear", href: "/modest-activewear" },
      { label: "Black hijabs", href: "/black-hijabs" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // ────────── TIER 1 SEO LANDING PAGES (competitive gap targets) ──────────

  // MODEST GRADUATION DRESSES (Vol 3,600 · KDI 18)
  {
    slug: "modest-graduation-dresses",
    primaryKeyword: "modest graduation dresses",
    title: "Modest Graduation Dresses — Long Sleeve & Floor Length | HijabiStore",
    metaDescription:
      "Shop modest graduation dresses with long sleeves, full coverage & floor-length hems. White, ivory, navy & pastel styles cut for hijabis. Free US shipping $75+.",
    h1: "Modest Graduation Dresses for Hijabis",
    heroSubtitle:
      "Long-sleeve, full-coverage graduation dresses designed to flow beautifully under your gown — and look incredible at the photoshoot after.",
    heroImageAlt: "Modest long-sleeve white graduation dress with floor-length skirt",
    categorySlugs: ["modest-dresses", "modest-eveningwear", "dresses"],
    productKeywords: ["graduation dress", "white dress", "modest dress", "long sleeve dress", "maxi dress"],
    intro:
      "You've worked years for this walk across the stage — the last thing you need is a dress that fights your hijab or rides up under the gown. We built this edit with hijabis in mind: full-length hems that fall straight under academic robes, sleeves that taper at the wrist so they don't bunch at the elbow, and lightweight breathable fabrics that won't have you melting under polyester. Most pieces here pull double duty — wear them again to Eid, engagements and dinners long after the cap toss.",
    sections: [
      {
        heading: "What to wear under a graduation gown (modest edition)",
        body:
          "The gown adds heat fast, so pick breathable fabric — modal, viscose, or a lightweight crepe. Skip anything sequinned or beaded along the shoulders; it'll catch on the gown and ruin the photos. A fitted long-sleeve top under a sleeveless midi or maxi dress works just as well as a long-sleeve dress, and gives you more options to re-wear the pieces afterward.\n\nFor color, white and ivory are the classics for a reason — they pop against the dark gown in every photo. But navy, sage, dusty rose, and champagne are all rising fast in our graduation collection because they photograph as elegant without being too bridal.",
      },
      {
        heading: "Modest graduation dress styles that actually work",
        body:
          "Long-sleeve maxi dresses are the safest choice — full coverage, floor-grazing hem peeking out under the gown, easy to belt and dress up.\n\nLong-sleeve midi dresses paired with knee-high boots or flats are perfect for outdoor graduations where you'll be walking on grass.\n\nA-line modest dresses with a defined waist photograph the best in group shots.\n\nFor the dinner afterward, an embellished long-sleeve gown or a satin slip dress layered over a long-sleeve top turns the day into an evening look.",
      },
      {
        heading: "Coordinating your hijab with the dress",
        body:
          "Ivory and champagne hijabs are universally flattering against academic robes. For colored dresses, tonal hijabs (a shade lighter or darker than the dress) read more elevated than contrast colors. Chiffon or modal hijabs sit best under the graduation cap — jersey can sometimes bunch where the elastic sits.",
      },
    ],
    faqs: [
      { q: "What should hijabis wear to graduation?", a: "A modest long-sleeve maxi or midi dress in a lightweight fabric like modal or crepe, paired with a hijab in a tonal or neutral shade. Skip anything beaded on the shoulders so the gown sits flat." },
      { q: "Is white the only color for graduation dresses?", a: "Not at all. Ivory, champagne, navy, sage, and dusty rose all photograph beautifully against an academic gown and feel more personal than classic white." },
      { q: "What fabric is best for a graduation dress?", a: "Modal, viscose, lightweight crepe, or cotton blends. The gown traps heat, so breathable fabric is non-negotiable. Avoid heavy satin or velvet unless your ceremony is indoors and short." },
      { q: "Can I wear long sleeves under the gown?", a: "Yes — long sleeves work beautifully under a graduation gown. Just make sure the sleeve isn't too wide so it doesn't bunch at the elbow when you raise your arm." },
      { q: "What dress length is best for graduation?", a: "Floor-length or ankle-length is most flattering — the dress peeks out from under the gown and gives a fuller silhouette in photos. Midi dresses with boots also work well for outdoor ceremonies." },
      { q: "Do you offer plus-size modest graduation dresses?", a: "Yes — our vendors stock through US 24+ with long-sleeve and full-coverage options across every size range." },
    ],
    related: [
      { label: "Modest white dresses", href: "/modest-white-dress" },
      { label: "Modest evening wear", href: "/modest-eveningwear" },
      { label: "Modest wedding guest dresses", href: "/modest-wedding-guest-dresses" },
      { label: "Modest dresses", href: "/modest-dresses" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // MODEST WHITE DRESS (Vol 1,900 · KDI 12)
  {
    slug: "modest-white-dress",
    primaryKeyword: "modest white dresses",
    title: "Modest White Dresses — Long Sleeve, Maxi & Bridal | HijabiStore",
    metaDescription:
      "Shop modest white dresses — long-sleeve maxis, bridal & evening styles with full coverage. Ivory, cream & off-white tones. Free US shipping over $75.",
    h1: "Modest White Dresses — Long Sleeve & Full Coverage",
    heroSubtitle:
      "From everyday white maxis to nikkah-ready gowns: long-sleeve, opaque, beautifully draped modest white dresses for every occasion.",
    heroImageAlt: "Modest long-sleeve white maxi dress on a soft neutral background",
    categorySlugs: ["modest-dresses", "modest-eveningwear", "dresses"],
    productKeywords: ["white dress", "modest dress", "ivory dress", "bridal", "maxi dress"],
    intro:
      "The right modest white dress goes everywhere — nikkah, walima, engagement dinners, graduation, Eid mornings, Sunday brunch in the garden. The wrong one is sheer in daylight, too short in the sleeve, or that flat shade of white that washes out every skin tone. Every piece in this edit has been pulled aside by our buying team for three things only: proper opacity in real light, full wrist-and-ankle coverage, and a tone of white (or ivory, or cream) that actually looks good in photos. Everything else is up to you.",
    sections: [
      {
        heading: "How to choose a modest white dress",
        body:
          "Start with the fabric. Crepe and ponte give structure and read as elevated. Cotton voile, modal, and linen blends are airy and forgiving in heat. Satin and silk look luxurious for evening but always need a slip underneath. Chiffon should be fully lined — single-layer chiffon in white is rarely opaque enough.\n\nThen think about the shade. Pure white is sharp and reads bridal — gorgeous for nikkah but can look harsh in everyday photos. Ivory and cream are warmer, more universally flattering, and pair better with neutral hijabs. Off-white with a subtle texture (lace, jacquard, embroidery) adds depth without losing the clean look.",
      },
      {
        heading: "Modest white dress styles for every occasion",
        body:
          "Long-sleeve maxi white dresses are the workhorse — perfect for graduation, engagement, day events.\n\nWhite kaftan dresses with embroidery or beading work beautifully for nikkah and walima.\n\nWhite midi shirt dresses are an easy daytime pick — throw a tan belt on for shape.\n\nA-line and empire-waist white gowns are the most flattering silhouette across body types.",
      },
      {
        heading: "Styling tips: hijab pairings for modest white dresses",
        body:
          "Cream, blush, and dusty rose hijabs are the most photogenic with white. Pure white hijab on a white dress can look monotone — go a half-shade off for dimension. For evening, a champagne or soft gold hijab elevates the whole look without competing.",
      },
    ],
    faqs: [
      { q: "Are modest white dresses see-through?", a: "Many off-the-shelf white dresses are — that's why we screen for opacity. Crepe, ponte, lined satin, and double-layer chiffon are your safest bets. Always check the listing's fabric details." },
      { q: "What white dress is best for a nikkah?", a: "An embroidered or beaded long-sleeve gown in ivory or off-white photographs beautifully and gives you the bridal feel without being a full wedding gown. Many of our vendors stock nikkah-specific cuts." },
      { q: "Can I wear white to someone else's wedding?", a: "In most modest wedding traditions it's considered inappropriate unless the bride specifically asks. Choose champagne, blush, or dusty pastels instead." },
      { q: "What's the difference between white and ivory?", a: "White is pure and reads cooler. Ivory has a warm yellow undertone and tends to be more flattering against most skin tones. Cream sits between the two with a soft, peachy warmth." },
      { q: "Do you offer plus-size modest white dresses?", a: "Yes — our vendors carry through US 24+ in long-sleeve, full-coverage white dresses." },
      { q: "Can I wash a white modest dress at home?", a: "Crepe, modal, and cotton blends machine-wash cold on delicate. Satin, silk, and embellished pieces should be dry-cleaned to preserve the white." },
    ],
    related: [
      { label: "Modest graduation dresses", href: "/modest-graduation-dresses" },
      { label: "Modest wedding guest dresses", href: "/modest-wedding-guest-dresses" },
      { label: "Modest evening wear", href: "/modest-eveningwear" },
      { label: "Modest dresses", href: "/modest-dresses" },
    ],
  },

  // PRAYER CLOTHES (Vol 320 · KDI 8) — complements existing /prayer-dresses
  {
    slug: "prayer-clothes",
    primaryKeyword: "prayer clothes",
    title: "Prayer Clothes for Women — Salah-Ready Modest Wear | HijabiStore",
    metaDescription:
      "Shop prayer clothes for women — two-piece prayer sets, one-piece prayer dresses, khimars & lightweight prayer hijabs. Travel pouches included. Free US shipping $75+.",
    h1: "Prayer Clothes for Women — One-Piece & Two-Piece Sets",
    heroSubtitle:
      "Dedicated, easy-on prayer clothes that take the guesswork out of wudu and salah — at home, at work, traveling, or visiting.",
    heroImageAlt: "White two-piece prayer set with khimar and full-length skirt folded with travel pouch",
    categorySlugs: ["prayer-garments", "prayer-accessories-kits", "prayer-dresses"],
    productKeywords: ["prayer", "salah", "namaz", "khimar", "prayer dress", "prayer set"],
    intro:
      "A dedicated set of prayer clothes is one of those small lifestyle shifts that quietly changes everything — you stop rushing wudu before maghrib, stop praying in whatever you happen to have on, stop wondering if your sleeves rode up during sujood. The set lives in a small pouch in your bag, your office drawer, your suitcase, and slips on in under a minute. This collection spans minimalist two-piece sets, pullover one-piece prayer dresses, packable travel kits and kid-sized versions — sourced from independent brands who pray in their own designs.",
    sections: [
      {
        heading: "Types of prayer clothes",
        body:
          "Two-piece prayer sets are the most popular — a long pullover khimar that covers head to waist or knee, plus a matching ankle-length skirt. Quick to put on, easy to store, breathable in summer.\n\nOne-piece prayer dresses are a single pullover garment that covers from head to ankle. Fewer pieces to manage, even faster to wear, great for travel.\n\nLightweight prayer hijabs paired with your existing abaya are the minimal option — slip-on, no pins, designed specifically for salah.\n\nChildren's prayer clothes in colorful prints help young kids feel excited about learning salah.",
      },
      {
        heading: "What to look for in prayer clothes",
        body:
          "Opaque fabric — single-layer chiffon won't cut it; look for crepe, modal blends, or lined polyester.\n\nFull coverage from head to wrist to ankle — sleeves should not ride up during ruku and sujud.\n\nElastic or loose-fit waist on the skirt so it stays put when standing back up.\n\nA travel pouch — the small things matter. The brands we work with include foldable pouches so the set lives in your handbag.",
      },
      {
        heading: "Caring for prayer clothes",
        body:
          "Machine wash cold on delicate, hang to dry. Iron on low if needed. Most of our prayer clothes are designed to be wrinkle-resistant so you can pull them straight out of the pouch and pray.",
      },
    ],
    faqs: [
      { q: "What's the difference between prayer clothes and prayer dresses?", a: "Prayer clothes is the broader category — it includes one-piece prayer dresses, two-piece prayer sets, prayer hijabs, and accessory kits. A prayer dress specifically refers to a single pullover garment." },
      { q: "Can I pray in any abaya or modest dress?", a: "Yes, as long as it covers everything required and is opaque. Dedicated prayer clothes just make it faster, cleaner, and easier — especially when traveling or at work." },
      { q: "Are two-piece prayer sets better than one-piece?", a: "Two-piece sets are more breathable and easier to clean. One-piece prayer dresses are faster to put on and have one less thing to coordinate. Both are valid — pick what fits your routine." },
      { q: "What size should I order?", a: "Most prayer clothes are loose-fitting and one-size or sized S/M/L. Always check the length — for tall sisters look for 'tall' or 'maxi' length options." },
      { q: "Do you offer children's prayer clothes?", a: "Yes — our prayer collection includes sizes from toddler through teen, often in fun prints that help young kids look forward to salah." },
      { q: "Are prayer clothes good for traveling?", a: "Absolutely — they're one of the most travel-friendly pieces you can own. Roll them into the included pouch and they take up almost no space in carry-on." },
    ],
    related: [
      { label: "Prayer dresses", href: "/prayer-dresses" },
      { label: "Prayer accessories & kits", href: "/prayer-accessories-kits" },
      { label: "Children's prayer wear", href: "/prayer-childrens" },
      { label: "Umrah clothing", href: "/umrah-clothing" },
      { label: "Abayas", href: "/abayas" },
    ],
  },

  // HIJAB CAPS (Vol 590 · KDI 10)
  {
    slug: "hijab-caps",
    primaryKeyword: "hijab caps",
    title: "Hijab Caps — Underscarves, Tie-Back & Ninja Caps | HijabiStore",
    metaDescription:
      "Shop hijab caps — tie-back, full-coverage, ninja & jersey underscarves to keep your hijab in place all day. 30+ colors. Free US shipping over $75.",
    h1: "Hijab Caps & Underscarves",
    heroSubtitle:
      "The unsung hero of every hijabi's wardrobe. Tie-back, tube, ninja and full-coverage hijab caps to anchor your scarf and keep your hairline tucked.",
    heroImageAlt: "Stack of jersey hijab caps and underscarves in neutral colors",
    categorySlugs: ["underscarves-inner-caps", "underscarves-hijab-caps", "underscarves"],
    productKeywords: ["hijab cap", "underscarf", "ninja cap", "bonnet", "inner cap"],
    intro:
      "Ask any long-time hijabi what changed their everyday wrap the most, and the honest answer is rarely a new scarf — it's the cap underneath. A good cap grips the hairline, absorbs scalp oil, gives sheer fabrics the opacity they need, and stops the constant micro-adjusting that exhausts the back of your mind all day. This collection covers every style the question \"what's under your hijab?\" actually means: tube bands, tie-back crowns, full-coverage hoods and ninja caps, in jersey, cotton, modal and bamboo blends chosen to grip without itching.",
    sections: [
      {
        heading: "Types of hijab caps explained",
        body:
          "Tube caps are the simplest — a stretchy elastic-free band that sits across the forehead. Best for jersey hijabs that already grip.\n\nTie-back caps have a full crown plus ties at the back to adjust fit. The most secure for slippery fabrics like chiffon and silk.\n\nFull-coverage hijab caps extend down the neck like a small balaclava — perfect for summer when you don't want to layer fabric, or for sports.\n\nNinja caps are tight-fitting one-piece caps that cover from forehead to chin and down the neck. Often worn under a square hijab or shayla for full opacity and no slipping.",
      },
      {
        heading: "How to choose the right hijab cap",
        body:
          "Match the cap fabric to your hijab fabric. Slippery hijab? Cotton or jersey cap for grip. Heavy hijab? Bamboo or lightweight modal cap so you don't overheat. Always go opaque under chiffon and lace — a thin cap defeats the point.\n\nFor color, build a base of black, white, nude, and brown caps to match every hijab. Skin-tone (nude) caps are essential because they disappear under sheer fabric.",
      },
      {
        heading: "Hijab cap care",
        body:
          "Hijab caps see a lot of wear and absorb oils, so wash them more often than your hijab — every 2-3 wears. Cold wash, gentle detergent, air dry. They lose their stretch faster than people expect, so rotate 4-6 caps instead of one favorite.",
      },
    ],
    faqs: [
      { q: "Do I really need a hijab cap?", a: "If your hijab slips, your hair shows at the hairline, or your scalp gets oily quickly — yes. A good cap fixes all three." },
      { q: "What's the difference between a hijab cap, underscarf, and bonnet?", a: "They're often used interchangeably. \"Underscarf\" is the broader term; \"hijab cap\" usually means a more structured fitted cap; \"bonnet\" sometimes refers to a fuller crown cap used for sleep or under bigger styles." },
      { q: "Which hijab cap is best for slippery hijabs?", a: "Tie-back caps with cotton or jersey crowns are the most secure. The ties let you adjust tightness, and the textured fabric grips chiffon and silk." },
      { q: "Can I sleep in a hijab cap?", a: "Yes — many hijabis wear a satin-lined cap or bonnet at night to protect hair from breakage. Look for satin-lined options in our edit." },
      { q: "What size hijab cap should I order?", a: "Most caps are one-size with elastic. If you're between sizes or have a smaller/larger head, look for 'small' or 'XL' labeled options." },
      { q: "Are ninja caps better than regular caps?", a: "For full opacity and zero slipping, yes — but they can feel restrictive. Try one for special-occasion wraps or sports first before making it your daily." },
    ],
    related: [
      { label: "Ninja hijab caps", href: "/ninja-hijab-cap" },
      { label: "All underscarves", href: "/underscarves" },
      { label: "Underscarves & hijab caps", href: "/underscarves-hijab-caps" },
      { label: "Hijab pins & magnets", href: "/hijab-pins-magnets" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },

  // NINJA HIJAB CAP (Vol 40 · KDI 8) — long-tail authority play
  {
    slug: "ninja-hijab-cap",
    primaryKeyword: "ninja hijab cap",
    title: "Ninja Hijab Caps — Full-Coverage Underscarf | HijabiStore",
    metaDescription:
      "Shop ninja hijab caps — full-face & neck-coverage underscarves for opaque, slip-free hijab styling. Jersey & cotton blends in 15+ colors. Free US shipping $75+.",
    h1: "Ninja Hijab Caps — Full-Coverage Underscarves",
    heroSubtitle:
      "The one-piece cap that covers forehead, neck and chin in a single layer — for opaque, slip-free hijab wear under any fabric.",
    heroImageAlt: "Black ninja hijab cap with full forehead and neck coverage",
    categorySlugs: ["underscarves-inner-caps", "underscarves-hijab-caps", "underscarves"],
    productKeywords: ["ninja cap", "ninja hijab", "underscarf", "full coverage cap"],
    intro:
      "If you've ever pinned a chiffon hijab six different ways and still watched it inch back over the course of a workday, the ninja cap is the answer. It's a single stretch-fit hood that covers forehead, ears, neck and chin in one continuous piece — so the hijab on top has nothing to slide against and nothing to expose. Hijabis reach for ninja caps for three reasons: sheer fabrics that need an opaque base, Khaleeji and shayla drapes that rely on the cap to do the holding, and active days (gym, sports, travel) when you simply don't want to think about your hijab again until you take it off.",
    sections: [
      {
        heading: "When to wear a ninja cap",
        body:
          "Under sheer chiffon and lace hijabs — the ninja cap provides the opacity the hijab itself doesn't.\n\nFor Khaleeji and shayla styles where the hijab drapes loosely and the cap does the holding.\n\nDuring sports, gym sessions, and travel days when you don't want to think about your hijab.\n\nFor formal events when you're wearing a delicate hijab that can't take pins.",
      },
      {
        heading: "Ninja cap fabrics and fit",
        body:
          "Jersey and cotton blends are the most common — stretchy, breathable, gripping. Bamboo blends are softer for sensitive scalps. Look for a flat seam at the forehead (chunky seams show through thin hijabs) and a fit that's snug but not headache-tight.\n\nFor color, nude/skin-tone is the most versatile under sheer hijabs because it disappears. Black and white cover the rest of the wardrobe.",
      },
      {
        heading: "How to wear a ninja cap",
        body:
          "Pull the cap on like a balaclava, making sure the forehead edge sits where you want your hijab to start. Tuck any hair fully inside. Drape your hijab over the top — because the cap is grippy and full-coverage, you can skip pins entirely with most jersey hijabs, or use just one pin under the chin for chiffon.",
      },
    ],
    faqs: [
      { q: "What is a ninja hijab cap?", a: "A ninja hijab cap is a one-piece stretchy cap that covers your forehead, ears, neck and chin in a single layer — leaving only your face exposed. It's worn underneath a hijab for full opacity and grip." },
      { q: "Is a ninja cap the same as an underscarf?", a: "Ninja caps are a type of underscarf, but they're more full-coverage than regular tube or tie-back caps. Most underscarves stop at the hairline; ninja caps continue down the neck." },
      { q: "Will a ninja cap give me a headache?", a: "Only if it's too tight. Look for jersey or bamboo blends with stretch, and size up if you're between sizes. A well-fitted ninja cap should be snug, not tight." },
      { q: "Can I wear a ninja cap to the gym?", a: "Yes — ninja caps are ideal for sports because they don't slip during movement. Pair with a sports hijab over the top for full activewear coverage." },
      { q: "What color ninja cap should I buy first?", a: "Nude or skin-tone — it's invisible under sheer hijabs and works as a base for almost any colored hijab on top." },
      { q: "How do I wash a ninja hijab cap?", a: "Cold machine wash on delicate or hand wash. Air dry flat — the dryer breaks down the elastic faster than you'd think." },
    ],
    related: [
      { label: "All hijab caps", href: "/hijab-caps" },
      { label: "Underscarves", href: "/underscarves" },
      { label: "Sports hijabs", href: "/sports-hijabs-shop" },
      { label: "Hijab pins & magnets", href: "/hijab-pins-magnets" },
      { label: "All hijabs", href: "/hijabs" },
    ],
  },
];

export const getLandingPage = (slug: string): LandingPageContent | undefined =>
  LANDING_PAGES.find((p) => p.slug === slug);
